// ── AccountPage ──
import { Fragment, useState, useEffect, useCallback } from "react";
import { B } from "../../../shared/theme";
import { exportCSV, fmt, fmtDate, fmtRatio, fmtPhone, localFilter } from "../../../shared/format";
import { Badge, Btn, Card, ErrorMsg, FInput, FInputNum, Icon, Modal, PageLink, PanelTitle, Pagination, RiskBadge, STh, SearchBar, SectionHeader, Spinner, StatCard, TabBtn, Td, TdNum, Th, ThNum } from "../../../shared/ui";
import { usePagination, useSortable } from "../../../shared/hooks";
import { CollateralTab } from "../components/CollateralTab";
import { CovenantTab } from "../components/CovenantTab";
import { NotesPanel } from "../components/NotesPanel";
import { StatementModal, generatedAt } from "../components/StatementModal";
import { EmailActivityPanel } from "../../../shared/EmailActivityPanel";
import { useApp } from "../../../shared/appContext";
import { PortalBadge, portalState } from "../../../shared/portalAccess";
import { StateOfOrgBadge } from "../../../shared/entityIdentity";
import { MonitoringSummary, MonitoringTab } from "../components/AccountMonitoring";

// ── Closing checklist, scoped to ONE account ────────────────────────────────
//
// Two views of the same fetch, under the Terms card, because a checklist item
// hangs off a TERM: asking for one account's items any other way is a request
// per term, and the answer people actually want ("what is still outstanding on
// this borrower") spans every loan and term under the account.
//
// Card one is what is left to do. Card two is every item that has a document
// attached, open or closed — the paper trail, which is a different question
// from the work list and is asked at different times.
//
// Self-contained so the hooks live in a real component; the tab body that
// renders this is an IIFE, the same reason PlaidBankCards is its own component.
const AccountChecklistCards = ({ accountId }) => {
  const { api, role, setModal, setConfirm, delRec } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    if (!accountId) { setRows([]); setLoading(false); return; }
    setLoading(true); setErr(null);
    api.get("getClosingChecklist?accountid=" + accountId)
      // ALL mode answers with a bare array; the single-term mode answers with
      // { termid, items }. Take either rather than depending on which one the
      // query string happened to select.
      .then(d => { setRows(Array.isArray(d) ? d : (d && d.items) || []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  // ONE definition of "has a document", used by the filter below AND by the
  // cell that renders the link. Two copies drift: the first version trimmed
  // here and not in the cell, so a boxURL of "   " was excluded from the
  // documents card while the open card still offered a Link to nowhere.
  const hasDoc = (r) => !!(r.boxURL && String(r.boxURL).trim() !== "");

  const open    = rows.filter(r => !r.completeddate);
  const withDoc = rows.filter(hasDoc);

  const { sorted:openSorted, sortKey:openSK, sortDir:openSD, toggle:openToggle } = useSortable(open, 'fundingdate');
  const { sorted:docSorted,  sortKey:docSK,  sortDir:docSD,  toggle:docToggle  } = useSortable(withDoc, 'checklistitem');

  const rowProps = (it) => ({
    style: { cursor:"pointer" },
    onClick: () => setModal({ type:"checklistitem-view", rec:it }),
    onMouseEnter: e => e.currentTarget.style.background = "var(--color-background-secondary)",
    onMouseLeave: e => e.currentTarget.style.background = "",
  });
  const actions = (it) => (
    <Td><div style={{ display:"flex", gap:6, justifyContent:"flex-end" }} onClick={e => e.stopPropagation()}>
      <Btn onClick={()=>setModal({ type:"checklistitem-view", rec:it })}><Icon type="view" /></Btn>
      {role.canEdit && <Btn onClick={()=>setModal({ type:"checklistitem-edit", rec:it, onDone:load })}><Icon type="edit" /></Btn>}
      {role.canDelete && <Btn variant="danger" onClick={()=>setConfirm({
        msg:"Delete this checklist item?",
        onConfirm:()=>delRec("ClosingChecklistItems", it.id, "Deleted checklist item", load) })}><Icon type="delete" /></Btn>}
    </div></Td>
  );
  const loanCell = (it) => (
    <Td onClick={e => e.stopPropagation()}>
      {it.loanid
        ? <PageLink page="loan" params={{ accountid:it.accountid, loanid:it.loanid, companyname:it.companyname, loannumber:it.loannumber }}
                    style={{ fontFamily:"var(--font-mono)", fontSize:12, color:B.cyan }}>{it.loannumber}</PageLink>
        : <span style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{it.loannumber || "—"}</span>}
    </Td>
  );
  const docCell = (it) => (
    <Td onClick={e => e.stopPropagation()}>
      {hasDoc(it)
        ? <a href={String(it.boxURL).trim()} target="_blank" rel="noreferrer" data-doc="1" style={{ color:B.cyan }}>Link</a>
        : (it.documentrequired && !it.unrequiredocument)
          ? <span style={{ color:"#8A5A00", fontSize:11 }}>required</span>
          : "—"}
    </Td>
  );
  const empty = (msg, cols) => (
    <tr><td colSpan={cols} style={{ padding:"18px 12px", color:"var(--color-text-secondary)", fontSize:13 }}>{msg}</td></tr>
  );

  if (loading) return <div style={{ marginTop:16 }}><Spinner /></div>;
  if (err)     return <div style={{ marginTop:16 }}><ErrorMsg msg={err} onRetry={load} /></div>;

  return <>
    {/* ── Outstanding ── title inside the card, like the Overview panels */}
    <Card style={{ overflowX:"auto", marginTop:16 }}>
      <PanelTitle title="Open Closing Checklist Items" count={open.length} actions={[
        <Btn key="ex" onClick={()=>exportCSV(openSorted,
          ["loannumber","term_type","fundingdate","order","checklistcategory","checklistitem","responsibleparty","documentrequired","unrequiredocument","boxURL"],
          "open_checklist_items.csv",
          { documentrequired:v=>v?"Yes":"No", unrequiredocument:v=>v?"Yes":"No" })}>Export CSV</Btn>
      ]} />
        <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
          <thead><tr>
            <STh label="Loan #"       sortKey="loannumber"        currentKey={openSK} currentDir={openSD} onSort={openToggle} />
            <STh label="Term Type"    sortKey="term_type"         currentKey={openSK} currentDir={openSD} onSort={openToggle} />
            <STh label="Funding Date" sortKey="fundingdate"       currentKey={openSK} currentDir={openSD} onSort={openToggle} />
            <STh label="Order"        sortKey="order"             currentKey={openSK} currentDir={openSD} onSort={openToggle} numeric />
            <STh label="Category"     sortKey="checklistcategory" currentKey={openSK} currentDir={openSD} onSort={openToggle} />
            <STh label="Item"         sortKey="checklistitem"     currentKey={openSK} currentDir={openSD} onSort={openToggle} />
            <STh label="Resp."        sortKey="responsibleparty"  currentKey={openSK} currentDir={openSD} onSort={openToggle} />
            <STh label="Doc Req'd"    sortKey="documentrequired"  currentKey={openSK} currentDir={openSD} onSort={openToggle} />
            <Th>Document</Th>
            <Th></Th>
          </tr></thead>
          <tbody>
            {openSorted.length === 0
              ? empty(rows.length ? "Every checklist item on this account is complete." : "No closing checklist items on this account.", 10)
              : openSorted.map(it => (
                <tr key={it.id} {...rowProps(it)}>
                  {loanCell(it)}
                  <Td>{it.term_type || "—"}</Td>
                  <Td>{fmtDate(it.fundingdate)}</Td>
                  <TdNum>{it.order ?? "—"}</TdNum>
                  <Td>{it.checklistcategory || "—"}</Td>
                  <Td style={{ fontWeight:500 }}>{it.checklistitem || "—"}</Td>
                  <Td>{it.responsibleparty || "—"}</Td>
                  <Td>{it.unrequiredocument
                        ? <span style={{ color:"var(--color-text-secondary)" }}>waived</span>
                        : (it.documentrequired ? "✓" : "—")}</Td>
                  {docCell(it)}
                  {actions(it)}
                </tr>
              ))}
          </tbody>
        </table>
    </Card>

    {/* ── The paper trail ── */}
    <Card style={{ overflowX:"auto", marginTop:16 }}>
      <PanelTitle title="Checklist Items with Documents" count={withDoc.length} actions={[
        <Btn key="ex" onClick={()=>exportCSV(docSorted,
          ["loannumber","term_type","checklistcategory","checklistitem","responsibleparty","completeddate","completedby","boxURL"],
          "checklist_documents.csv")}>Export CSV</Btn>
      ]} />
        <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
          <thead><tr>
            <STh label="Loan #"       sortKey="loannumber"        currentKey={docSK} currentDir={docSD} onSort={docToggle} />
            <STh label="Term Type"    sortKey="term_type"         currentKey={docSK} currentDir={docSD} onSort={docToggle} />
            <STh label="Category"     sortKey="checklistcategory" currentKey={docSK} currentDir={docSD} onSort={docToggle} />
            <STh label="Item"         sortKey="checklistitem"     currentKey={docSK} currentDir={docSD} onSort={docToggle} />
            <STh label="Resp."        sortKey="responsibleparty"  currentKey={docSK} currentDir={docSD} onSort={docToggle} />
            <STh label="Completed"    sortKey="completeddate"     currentKey={docSK} currentDir={docSD} onSort={docToggle} />
            <STh label="Completed By" sortKey="completedby"       currentKey={docSK} currentDir={docSD} onSort={docToggle} />
            <Th>Document</Th>
            <Th></Th>
          </tr></thead>
          <tbody>
            {docSorted.length === 0
              ? empty("No checklist item on this account has a document attached yet.", 9)
              : docSorted.map(it => (
                <tr key={it.id} {...rowProps(it)}>
                  {loanCell(it)}
                  <Td>{it.term_type || "—"}</Td>
                  <Td>{it.checklistcategory || "—"}</Td>
                  <Td style={{ fontWeight:500 }}>{it.checklistitem || "—"}</Td>
                  <Td>{it.responsibleparty || "—"}</Td>
                  <Td>{it.completeddate ? <Badge s="Complete" /> : <span style={{ color:"var(--color-text-secondary)" }}>—</span>}</Td>
                  <Td>{it.completedby || "—"}</Td>
                  {docCell(it)}
                  {actions(it)}
                </tr>
              ))}
          </tbody>
        </table>
    </Card>
  </>;
};

const PlaidStatusPill = ({ status }) => {
  const active = status === "active";
  return <span style={{ background: active ? "#E1F5EE" : "#FAECE7", color: active ? "#0F6E56" : "#993C1D",
    borderRadius:999, padding:"2px 9px", fontSize:11, fontWeight:600, whiteSpace:"nowrap" }}>
    {active ? "Active" : "Not Active"}</span>;
};

// Plaid linked accounts + balances and transactions, scoped to ONE account.
// Rendered under the Bank Accounts tab's existing card. Self-contained so the
// hooks live in a real component (the tab body itself is an IIFE).
const PlaidBankCards = ({ accountId }) => {
  const { api } = useApp();
  const [conns,setConns]=useState(null); const [cLoad,setCLoad]=useState(true); const [cErr,setCErr]=useState(null);
  const [txns,setTxns]=useState(null);   const [tLoad,setTLoad]=useState(true); const [tErr,setTErr]=useState(null);
  const [bankFilter,setBankFilter]=useState("");

  const loadPlaid=()=>{
    setCLoad(true); setTLoad(true); setCErr(null); setTErr(null);
    api.get("plaidConnections",{ accountId, refresh:"false" }).then(d=>{setConns(d);setCLoad(false);}).catch(e=>{setCErr(e.message);setCLoad(false);});
    api.get("plaidAllTransactions",{ accountId }).then(d=>{setTxns(d);setTLoad(false);}).catch(e=>{setTErr(e.message);setTLoad(false);});
  };
  useEffect(()=>{ if(accountId) loadPlaid(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },[accountId]);

  const money = (v,cur) => (v===null||v===undefined||v==="") ? "—"
    : new Intl.NumberFormat("en-US",{ style:"currency", currency: cur||"USD" }).format(Number(v));

  // Linked accounts + latest balance (one row per bank account for this account).
  const balRows = (conns||[]).flatMap(it => (it.accounts||[]).map(a => ({
    institution: it.institutionName, status: it.status,
    name: a.name || a.officialName || "—", mask: a.mask, subtype: a.subtype || a.type,
    available: a.availableBalance, current: a.currentBalance, currency: a.currency, asof: a.balanceAsOf,
  })));
  const { sorted: balSorted, sortKey: balSK, sortDir: balSD, toggle: balToggle } = useSortable(balRows, 'institution');

  // Transactions (credit positive) with a bank-account label to filter by.
  //
  // THE FALLBACK USED TO READ "Account". It was reached whenever the server
  // could not name the bank account — which, before dbo.BankAccountLink, was
  // every migrated and every hand-imported transaction. The word looked like a
  // label rather than a failure, so nobody chased it. "Unmapped account" is the
  // same information stated honestly: it reads as something to fix, it groups
  // together in the bank filter, and it disappears the moment a servicer maps
  // the stream.
  const txnAll = (txns||[]).map(t => ({
    ...t,
    bankAccountLabel: (t.bankAccountName || "Unmapped account")
                      + (t.bankMask ? " ••" + t.bankMask : ""),
    signedAmount: t.amount == null ? null : -t.amount,
    statusLabel: t.pending ? "Pending" : "Posted",
  }));
  const bankOptions = Array.from(new Set(txnAll.map(t=>t.bankAccountLabel))).sort();
  const txnFiltered = bankFilter ? txnAll.filter(t=>t.bankAccountLabel===bankFilter) : txnAll;
  const { sorted: txnSorted, sortKey: txnSK, sortDir: txnSD, toggle: txnToggle } = useSortable(txnFiltered, 'txnDate', 'desc');
  const { paginated: txnDisplayed, page: txnPage, setPage: txnSetPage, totalPages: txnTotalPages, total: txnTotal, reset: txnReset } = usePagination(txnSorted, 50);

  return <>
    {/* Linked Accounts & Balances */}
    <Card style={{ marginTop:20, overflowX:"auto" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div style={{ fontSize:14, fontWeight:600 }}>Linked Accounts &amp; Balances <span style={{ fontWeight:400, color:"var(--color-text-secondary)", fontSize:12 }}>(Plaid)</span></div>
        <div style={{ display:"flex", gap:8 }}>
          <Btn onClick={loadPlaid}>Refresh</Btn>
          <Btn onClick={()=>exportCSV(balSorted,["institution","status","name","mask","subtype","available","current","currency","asof"],"plaid_balances.csv")}>Export CSV</Btn>
        </div>
      </div>
      {cLoad ? <div style={{fontSize:13,color:"var(--color-text-secondary)",padding:"6px 0"}}>Loading…</div>
       : cErr ? <ErrorMsg msg={cErr} onRetry={loadPlaid} />
       : balSorted.length===0 ? <div style={{fontSize:13,color:"var(--color-text-secondary)",padding:"6px 0"}}>No Plaid-linked accounts for this account yet.</div>
       : <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
          <thead><tr>
            <STh label="Institution" sortKey="institution" currentKey={balSK} currentDir={balSD} onSort={balToggle} />
            <STh label="Status"      sortKey="status"      currentKey={balSK} currentDir={balSD} onSort={balToggle} />
            <STh label="Bank Account" sortKey="name"       currentKey={balSK} currentDir={balSD} onSort={balToggle} />
            <STh label="Mask"        sortKey="mask"        currentKey={balSK} currentDir={balSD} onSort={balToggle} />
            <STh label="Type"        sortKey="subtype"     currentKey={balSK} currentDir={balSD} onSort={balToggle} />
            <STh label="Available"   sortKey="available"   currentKey={balSK} currentDir={balSD} onSort={balToggle} />
            <STh label="Current"     sortKey="current"     currentKey={balSK} currentDir={balSD} onSort={balToggle} />
            <STh label="As Of"       sortKey="asof"        currentKey={balSK} currentDir={balSD} onSort={balToggle} />
          </tr></thead>
          <tbody>{balSorted.map((r,i)=>(
            <tr key={i}>
              <Td>{r.institution||"—"}</Td>
              <Td><PlaidStatusPill status={r.status} /></Td>
              <Td>{r.name}</Td>
              <Td><span style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{r.mask ? "••"+r.mask : "—"}</span></Td>
              <Td>{r.subtype||"—"}</Td>
              <Td style={{ textAlign:"right" }}>{money(r.available,r.currency)}</Td>
              <Td style={{ textAlign:"right" }}>{money(r.current,r.currency)}</Td>
              <Td>{r.asof?fmtDate(r.asof):"—"}</Td>
            </tr>
          ))}</tbody>
        </table>}
    </Card>

    {/* Transactions */}
    <Card style={{ marginTop:20, overflowX:"auto" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, gap:12, flexWrap:"wrap" }}>
        <div style={{ fontSize:14, fontWeight:600 }}>Transactions <span style={{ fontWeight:400, color:"var(--color-text-secondary)", fontSize:12 }}>(Plaid)</span></div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <select value={bankFilter} onChange={e=>{ setBankFilter(e.target.value); txnReset(); }}
            style={{ padding:"6px 10px", fontSize:13, border:"1px solid var(--color-border, #d5dbe2)", borderRadius:6, fontFamily:"var(--font-sans)" }}>
            <option value="">All bank accounts</option>
            {bankOptions.map(o=><option key={o} value={o}>{o}</option>)}
          </select>
          <Btn onClick={loadPlaid}>Refresh</Btn>
          <Btn onClick={()=>exportCSV(txnSorted,["txnDate","bankAccountLabel","name","merchantName","pfcPrimary","signedAmount","statusLabel"],"plaid_transactions.csv")}>Export CSV</Btn>
        </div>
      </div>
      {tLoad ? <div style={{fontSize:13,color:"var(--color-text-secondary)",padding:"6px 0"}}>Loading…</div>
       : tErr ? <ErrorMsg msg={tErr} onRetry={loadPlaid} />
       : txnSorted.length===0 ? <div style={{fontSize:13,color:"var(--color-text-secondary)",padding:"6px 0"}}>No transactions yet.</div>
       : <>
          <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
            <thead><tr>
              <STh label="Date"        sortKey="txnDate"          currentKey={txnSK} currentDir={txnSD} onSort={txnToggle} />
              <STh label="Bank Account" sortKey="bankAccountLabel" currentKey={txnSK} currentDir={txnSD} onSort={txnToggle} />
              <STh label="Description" sortKey="name"             currentKey={txnSK} currentDir={txnSD} onSort={txnToggle} />
              <STh label="Merchant"    sortKey="merchantName"     currentKey={txnSK} currentDir={txnSD} onSort={txnToggle} />
              <STh label="Category"    sortKey="pfcPrimary"       currentKey={txnSK} currentDir={txnSD} onSort={txnToggle} />
              <STh label="Amount"      sortKey="signedAmount"     currentKey={txnSK} currentDir={txnSD} onSort={txnToggle} />
              <STh label="Status"      sortKey="statusLabel"      currentKey={txnSK} currentDir={txnSD} onSort={txnToggle} />
            </tr></thead>
            <tbody>{txnDisplayed.map((t)=>(
              <tr key={t.id}>
                <Td>{t.txnDate?fmtDate(t.txnDate):"—"}</Td>
                <Td>{t.bankAccountLabel}</Td>
                <Td>{t.name||"—"}</Td>
                <Td>{t.merchantName||"—"}</Td>
                <Td>{t.pfcPrimary||t.category||"—"}</Td>
                <Td style={{ textAlign:"right" }}>{t.signedAmount==null?"—":fmt(t.signedAmount)}</Td>
                <Td>{t.statusLabel}</Td>
              </tr>
            ))}</tbody>
          </table>
          <Pagination page={txnPage} totalPages={txnTotalPages} total={txnTotal} pageSize={50} onPage={txnSetPage} />
        </>}
    </Card>
  </>;
};

// Statements tab body — sortable columns + a Type dropdown filter. Rendered as
// its own component so the sort/filter/paginate hooks live in a real component.
const StatementsTab = ({ statements, role, api, reload, delRec, setConfirm }) => {
  const [typeFilter, setTypeFilter] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [open, setOpen] = useState(null);   // { statement, mode }

  // The generated date is carried on the row rather than derived at render
  // time, so the column SORTS on the same value it SHOWS. useSortable reads
  // a[sortKey] and sends nulls to the bottom, so sorting on updatedat alone
  // would bury every migrated row under rows generated long before them.
  const rows = (statements || []).map(s => ({ ...s, _generated: generatedAt(s) }));
  const typeOptions = Array.from(new Set(rows.map(s => s.Type).filter(Boolean))).sort();
  const filtered = typeFilter ? rows.filter(s => s.Type === typeFilter) : rows;
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, 'As_of_Date', 'desc');
  const { paginated, page, setPage, totalPages, total, reset } = usePagination(sorted, 50);

  const togglePublish = async (s) => {
    setBusyId(s.id);
    try { await api.post("updateStatement", { id: s.id, publish: !s.Publish }); reload(); }
    catch (e) { alert(e.message); }
    finally { setBusyId(null); }
  };

  // Deleting the ROW does not delete the DOCUMENT. Nothing in this app removes
  // anything from Box, so the xlsx stays in the servicing folder and a later
  // batch will make a fresh row pointing at a fresh copy. The confirmation says
  // that outright, because "Delete" on a screen full of client-facing documents
  // reads as "remove the statement" unless it is told otherwise.
  const remove = (s) => setConfirm({
    msg: `Delete the ${s.Type || "statement"} record for ${s.As_of_Date ? fmtDate(s.As_of_Date) : "this period"}?`
       + (s.Publish ? " It is currently PUBLISHED and visible to the borrower." : "")
       + " The file itself stays in Box — only this record and its link are removed.",
    onConfirm: () => delRec('Statements', s.id,
      `Deleted statement record ${s.Type || ''} ${s.As_of_Date || ''}`.trim(), reload),
  });

  return <>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, gap:12, flexWrap:"wrap" }}>
      <div style={{ fontSize:12, color:"var(--color-text-secondary)", maxWidth:640 }}>
        Statements are created automatically when the Invoice / Interest Statement generators run and save to Box.
        A statement is only visible in the client portal once it is <strong>Published</strong>.
        Regenerating a period replaces the file and returns the statement to unpublished.
      </div>
      <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); reset(); }}
        style={{ padding:"6px 10px", fontSize:13, border:"1px solid var(--color-border, #d5dbe2)", borderRadius:6, fontFamily:"var(--font-sans)" }}>
        <option value="">All types</option>
        {typeOptions.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
    <Card style={{ overflowX:"auto" }}>
      {rows.length === 0
        ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:12 }}>No statements generated yet.</div>
        : <>
            <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
              <thead><tr>
                <STh label="As of Date" sortKey="As_of_Date" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Type"       sortKey="Type"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Doc Type"   sortKey="Doc_Type"   currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <Th>Box</Th>
                <STh label="Note"       sortKey="Note"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Status"     sortKey="Publish"    currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Generated"  sortKey="_generated" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <Th></Th>{/* publish toggle */}
                <Th></Th>{/* view / edit / delete */}
              </tr></thead>
              <tbody>{paginated.map(s => (
                <tr key={s.id}>
                  <Td>{s.As_of_Date ? fmtDate(s.As_of_Date) : "—"}</Td>
                  <Td style={{ fontWeight:500 }}>{s.Type || "—"}</Td>
                  <Td style={{ textTransform:"uppercase", fontSize:12 }}>{s.Doc_Type || "—"}</Td>
                  <Td>{s.Box_URL ? <a href={s.Box_URL} target="_blank" rel="noreferrer" style={{ color:B.cyan }}>Open</a> : "—"}</Td>
                  <Td>{s.Note || "—"}</Td>
                  <Td>
                    <span style={{ fontSize:11, padding:"2px 9px", borderRadius:999, fontWeight:600,
                      background: s.Publish ? "#E1F5EE" : "#FAECE7", color: s.Publish ? "#0F6E56" : "#993C1D" }}>
                      {s.Publish ? "Published" : "Not Published"}
                    </span>
                  </Td>
                  <Td>{s._generated ? fmtDate(s._generated) : "—"}</Td>
                  <Td>
                    {role.canEdit && <Btn onClick={() => togglePublish(s)}>
                      {busyId === s.id ? "…" : (s.Publish ? "Unpublish" : "Publish")}
                    </Btn>}
                  </Td>
                  {/* Same markup as the Contacts / Loans / Borrowers tables above:
                      eye, pencil, bin, in that order, danger on the bin. Publish
                      is a word rather than an icon because it is the one action
                      here that changes what a BORROWER can see, and it stays in
                      its own column so it is never a mis-click away from delete. */}
                  <Td><div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
                    <Btn onClick={() => setOpen({ statement: s, mode: "view" })}><Icon type="view" /></Btn>
                    {role.canEdit && <Btn onClick={() => setOpen({ statement: s, mode: "edit" })}><Icon type="edit" /></Btn>}
                    {role.canDelete && <Btn variant="danger" onClick={() => remove(s)}><Icon type="delete" /></Btn>}
                  </div></Td>
                </tr>
              ))}</tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={50} onPage={setPage} />
          </>}
    </Card>

    {open && <StatementModal statement={open.statement} mode={open.mode} api={api}
                             onClose={() => setOpen(null)} onSaved={reload} />}
  </>;
};

// ── UCC status of one borrower/guarantor, from the account's filings ──
// A filing counts for the entity when it was linked to it, or — for filings
// confirmed against the borrower without picking an entity — when the debtor
// name on the filing is the entity's name, ignoring punctuation, spacing and
// LLC/Inc-style endings. The best state wins: any live filing makes it Active;
// otherwise Lapsed beats Terminated; no filing is "None".
// Same reduction as dbo.fnUCCNameKey in SQL (the register's name check), so
// the two never disagree about whether two names are the same.
const normEntity = s => {
  let t = " " + String(s || "").toUpperCase().trim() + " ";
  t = t.replace(/&/g, " AND ").replace(/[.,'’"()\-\/;:\t]/g, " ").replace(/\s+/g, " ");
  for (const w of ["L L C","L L P","L P","P L L C","P C","LLC","LLP","LP","PLLC","PC","INC","INCORPORATED",
                   "CORP","CORPORATION","CO","COMPANY","LTD","LIMITED"]) t = t.split(" " + w + " ").join(" ");
  if (t.startsWith(" THE ")) t = t.slice(4);
  return t.replace(/ /g, "");
};
const UCC_TONES = {
  Active:     { bg:"#E1F5EE", fg:"#0F6E56" },
  Lapsed:     { bg:"#FBE4DD", fg:"#993C1D" },
  Terminated: { bg:"#F1EFE8", fg:"#888780" },
  None:       { bg:"transparent", fg:"var(--color-text-secondary)" },
};
const uccStatusFor = (bg, filings) => {
  const n = normEntity(bg.borrowerguarantorname);
  const mine = (filings || []).filter(f => (f.borrowerguarantorid && String(f.borrowerguarantorid) === String(bg.id))
    || (!f.borrowerguarantorid && n && normEntity(f.debtorname_asfiled) === n));
  const live = mine.filter(f => f.status !== "Terminated" && f.status !== "LAPSED");
  const label = live.length ? "Active"
    : mine.some(f => f.status === "LAPSED") ? "Lapsed"
    : mine.length ? "Terminated" : "None";
  const tip = mine.length
    ? mine.map(f => `${f.jurisdiction_state || ""} ${f.filingnumber || ""} — ${f.status}${f.lapsedate ? ", lapses " + String(f.lapsedate).slice(0, 10) : ""}`).join("\n")
    : "No UCC filing in the register for this entity";
  return { label, tip, filings: mine, ...UCC_TONES[label] };
};

export const AccountPage = () => {
    const { api, role, view, go, rowNav, hrefFor, tab, setTab, setModal, setConfirm, delRec } = useApp();
    // Who gets the Emails tab. Mirrors canReadEmailActivity in
    // api/shared/salesScope.js — the API refuses the request independently, so
    // this only decides whether the tab is offered.
    const canSeeEmails = !!(role.isAdmin || role.isportfoliomanager);
    const [account, setAccount] = useState(null);
    const [loans, setLoans] = useState([]);
    const [terms, setTerms] = useState([]);
    const [draws, setDraws] = useState([]);
    const [spmts, setSpmts] = useState([]);
    const [rpmts, setRpmts] = useState([]);
    const [legalEvents, setLegalEvents] = useState([]);
    const [statements, setStatements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [qLoans, setQLoans] = useState("");
    const [qTerms, setQTerms] = useState("");
    const [qDraws, setQDraws] = useState("");
    const [qSpmts, setQSpmts] = useState("");
    const [qRpmts, setQRpmts] = useState("");
    const [contacts, setContacts] = useState([]);
    const [loanFunds, setLoanFunds] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [achCollections, setAchCollections] = useState([]);
    const [borrowersGuarantors, setBorrowersGuarantors] = useState([]);
    // Scheduled Pmts grid-edit (Administrator only)
    const [spEdit, setSpEdit] = useState(false);
    const [spEdits, setSpEdits] = useState({});   // { [rowId]: { field: value } }
    const [spBusy, setSpBusy] = useState(false);
    const [spErr, setSpErr] = useState(null);


    const load = useCallback(() => {
      setLoading(true);
      Promise.all([
            api.get("getAccount?id="+view.accountid),
            api.get("getLoans?accountid="+view.accountid),
            api.get("getTerms?accountid="+view.accountid),
            api.get("getDraws?accountid="+view.accountid),
            api.get("getScheduledPmts?accountid="+view.accountid),
            api.get("getReceivedPmts?accountid="+view.accountid),
            api.get("getLegalEvents?accountid="+view.accountid),
            api.get("getContacts?accountid="+view.accountid),
            api.get("getLoanFunds?accountid=" + view.accountid),
            api.get("getBorrowersGuarantors?accountid=" + view.accountid),
            api.get("getBankAccounts?accountid=" + view.accountid),
            api.get("getStatements?accountid=" + view.accountid),
            api.get("getACHCollections?accountid=" + view.accountid),
          ]).then(([acct, ls, ts, ds, sps, rps, les, cts, lf, bgs, bas, sts, ach]) => {
            setAccount(acct);
            setLoans(ls); setTerms(ts); setDraws(ds);
            setSpmts(sps); setRpmts(rps); setLegalEvents(les); setContacts(cts); setLoanFunds(lf);
            setBorrowersGuarantors(bgs); setBankAccounts(bas); setStatements(sts); setAchCollections(Array.isArray(ach)?ach:[]);
            setLoading(false);
          }).catch(e => { setErr(e.message); setLoading(false); });
    }, []);
    useEffect(() => { load(); }, [load]);

    // UCC filings for this account, for the Relationships tab's "UCC Status"
    // column. Separate from load(): the register is for the monitoring audience
    // only (the endpoint refuses anyone else), and a failure here must not
    // blank the account page.
    const [uccFilings, setUccFilings] = useState(null);
    useEffect(() => {
      if (!role.canViewMonitoring || !view.accountid) { setUccFilings(null); return; }
      api.get("getUCCFilings", { accountid: view.accountid })
        .then(d => setUccFilings((d && d.rows) || [])).catch(() => setUccFilings(null));
    }, [view.accountid, role.canViewMonitoring]); // eslint-disable-line react-hooks/exhaustive-deps


    const filtLoans = localFilter(loans, qLoans, ["loannumber","loanstatus","facilitytype","collateraltype"]);
    const { sorted:sortedLoans, sortKey:loanSortKey, sortDir:loanSortDir, toggle:loanToggle } = useSortable(filtLoans, 'loannumber');
    const filtTerms = localFilter(terms, qTerms, ["loannumber","pipelinestage","facility_type","term_type"]);
    // The terms table is grouped by loan, so the sort runs INSIDE each group:
    // sortedTerms is ordered once and the loan loop filters it, which preserves
    // that order within each band. Sorting across the groups instead would mean
    // the loan headings no longer bound anything.
    const { sorted:sortedTerms, sortKey:termSortKey, sortDir:termSortDir, toggle:termToggle } = useSortable(filtTerms, 'loannumber');
    const filtDraws = localFilter(draws, qDraws, ["loannumber","drawrequestedbyName","drawfundedbyName"]);
    const { sorted:sortedDraws, sortKey:drawSortKey, sortDir:drawSortDir, toggle:drawToggle } = useSortable(filtDraws, 'loannumber');
    const { paginated:displayedDraws, page:drawPage, setPage:setDrawPage, totalPages:drawTotalPages, total:drawTotal, reset:resetDrawPage } = usePagination(sortedDraws, 50);
    const { sorted:sortedAch, sortKey:achSortKey, sortDir:achSortDir, toggle:achToggle } = useSortable(achCollections, 'loannumber');

    const filtSpmts = localFilter(spmts, qSpmts, ["scheduleddate","status","loannumber","companyname","feetype","othertype"])
      .sort((a,b) => new Date(a.scheduleddate) - new Date(b.scheduleddate));

    // ── Scheduled Pmts inline grid edit (Administrator only) ──
    const SP_NUM_FIELDS = ["scheduledprincipal","scheduledinterest","scheduledfee","scheduledother","scheduledpassthrough"];
    // current value for a field — edited value if present, else the loaded record value
    const spv = (r, field) => { const e = spEdits[r.id]; return (e && field in e) ? e[field] : r[field]; };
    const spLiveTotal = (r) => SP_NUM_FIELDS.reduce((s,f)=>s+(parseFloat(spv(r,f))||0),0);
    const setSpField = (id, field, val) => setSpEdits(m => ({ ...m, [id]: { ...(m[id]||{}), [field]: val } }));
    const spRowChanged = (s) => { const e = spEdits[s.id]; return e && Object.keys(e).some(k => String(e[k] ?? "") !== String(s[k] ?? "")); };
    const startSpEdit = () => { setSpErr(null); setSpEdits({}); setSpEdit(true); };
    const cancelSpEdit = () => { setSpErr(null); setSpEdits({}); setSpEdit(false); };
    const saveSpGrid = async () => {
      setSpBusy(true); setSpErr(null);
      try {
        const changed = spmts.filter(spRowChanged);
        // Post the FULL record with edits merged so orig* and untouched fields are preserved.
        await Promise.all(changed.map(s => api.post("saveScheduledPmt", { ...s, ...spEdits[s.id], id: s.id, loanid: s.loanid })));
        setSpEdit(false); setSpEdits({});
        load();
      } catch (e) { setSpErr(e.message || "Save failed"); }
      setSpBusy(false);
    };

    const filtRpmts = localFilter(rpmts, qRpmts, ["receiveddate","loannumber"]);
    const { sorted:sortedRpmts, sortKey:rpmtSortKey, sortDir:rpmtSortDir, toggle:rpmtToggle } = useSortable(filtRpmts, 'receiveddate', 'desc');
    const { paginated:displayedRpmts, page:rpmtPage, setPage:setRpmtPage, totalPages:rpmtTotalPages, total:rpmtTotal, reset:resetRpmtPage } = usePagination(sortedRpmts, 50);
    // ── Sortable headers for the tables that had plain ones ──
    // Contacts, Borrowers/Guarantors, Bank Accounts, Legal Events and Scheduled
    // Pmts now sort like the rest of the page. Badges that are worked out on
    // the fly (portal access, UCC status, bank-account count) get a sort key
    // of their own (_portal, _ucc, _banks) so the column orders by what it shows.
    // Grouped tables (bank accounts by borrower, scheduled payments by loan)
    // sort WITHIN each group, the same rule as Terms.
    const contactRows = contacts.map(c => ({ ...c, _portal: portalState(c).label }));
    const { sorted:sortedContacts, sortKey:ctSK, sortDir:ctSD, toggle:ctToggle } = useSortable(contactRows, 'fullname');
    const bgRows = borrowersGuarantors.map(bg => ({ ...bg,
      _ucc: uccFilings ? uccStatusFor(bg, uccFilings).label : null,
      _banks: bankAccounts.filter(b => b.borrowerid === bg.id).length }));
    const { sorted:sortedBG, sortKey:bgSK, sortDir:bgSD, toggle:bgToggle } = useSortable(bgRows, 'borrowerguarantorname');
    const { sorted:sortedBanks, sortKey:baSK, sortDir:baSD, toggle:baToggle } = useSortable(bankAccounts, 'bankname');
    const legalRows = legalEvents.map(e => ({ ...e, _level: e.loanid ? "Loan" : "Account" }));
    const { sorted:sortedLegal, sortKey:leSK, sortDir:leSD, toggle:leToggle } = useSortable(legalRows, 'startdate', 'desc');
    const { sorted:sortedSpmts, sortKey:spSK, sortDir:spSD, toggle:spToggle } = useSortable(filtSpmts, 'scheduleddate');

    const accountOriginalFundingDate = (() => {
        const ds = loans.map(l => l.originalfundingdate).filter(Boolean).sort();
        return ds[0] || null;                       // earliest funding across the loans
      })();
    const accountMaxMaturityDate = (() => {
        const ds = loans.map(l => l.maturitydate).filter(Boolean).sort();
        return ds[ds.length - 1] || null;           // latest maturity across the loans
      })();


    if (loading) return <Spinner />;
    if (err) return <ErrorMsg msg={err} onRetry={load} />;
    if (!account) return <ErrorMsg msg="Account not found." />;

    return <>
      <SectionHeader actions={[
        <Btn key="inv" onClick={()=>setModal({ type:"invoice", accountid:account.id, companyname:account.companyname })}>
          🧾 Invoice
        </Btn>,
       
        <Btn key="is" onClick={()=>setModal({ type:"interest-statement", accountid:account.id, companyname:account.companyname })}>
          📄 Interest Statement
        </Btn>,

        role.canEdit && <Btn key="e" variant="primary" onClick={()=>setModal({type:"account-edit",rec:account,onDone:load})}>Edit</Btn>,

        // THE ONLY PLACE AN ACCOUNT IN FUNDING CAN BE DELETED. The accounts
        // list excludes "Funding in Progress" from its main table, and the
        // worklist above that table shows one row per TERM for an account that
        // has any — so its bin deletes a terms sheet, correctly, and there was
        // no route to the account itself at all.
        //
        // It refuses rather than cascades: deleteRecord blocks an Accounts
        // delete while anything hangs off it and answers with the counts, so
        // what the person sees here is "still has 1 loan, 12 notes attached"
        // rather than a borrower's history disappearing behind one click.
        //
        // onDone leaves for the accounts list. Reloading this page would be
        // reloading a record that no longer exists.
        role.canDelete && <Btn key="d" variant="danger" onClick={()=>setConfirm({
          msg: 'Delete "'+account.companyname+'"?',
          onConfirm: () => delRec("Accounts", account.id,
                                  "Deleted account "+account.companyname,
                                  () => go("accounts", {})) })}>Delete Account</Btn>
      ].filter(Boolean)} />
      
    
      {/* ── Account Summary Card ── */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:0, paddingBottom:20, borderBottom:"0px solid #116588" }}>
        <Card style={{ padding:16 }}>
          <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:12, paddingBottom:6, borderBottom:"1px solid "+B.cyanLight }}>Account Summary</div>
          {(()=>{
            const Row = ({ label, value }) => (
              <div style={{ display:"flex", gap:2, fontSize:12, marginBottom:5 }}>
                <span style={{ color:"var(--color-text-secondary)", fontSize:12, minWidth:90, flexShrink:0 }}>{label}:</span>
                <span style={{ fontWeight:500 }}>{value}</span>
              </div>
            );
            return <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
              <div>
                <Row label="Name"       value={account.companyname} />
                <Row label="Account #"  value={account?.accountnumber||"—"} />
                <Row label="Facility Type"  value={account.facilitytypes||"—"} />
                <Row label="Collateral Type"  value={account.collateraltypes||"—"} />
                <Row label="Website" value={account.website
                  ? <a href={account.website} target="_blank" rel="noreferrer" style={{ color:B.cyan }}>{account.website}</a>
                  : "—"} />
                {/* The account's business address, as kept on the account.
                    Street on its own line, then "City, ST 12345"; country only
                    when it is not the US. */}
                <Row label="Address" value={(() => {
                  const line2 = [account.city, [account.state, account.zip].filter(Boolean).join(" ")]
                    .filter(Boolean).join(", ");
                  const country = account.country && !/^(us|usa|united states( of america)?)$/i.test(String(account.country).trim())
                    ? account.country : null;
                  const lines = [account.street, line2, country].filter(Boolean);
                  return lines.length ? <span>{lines.map((l, i) => <div key={i}>{l}</div>)}</span> : "—";
                })()} />

              </div>
              <div>
                <Row label="Status"   value={<Badge s={account.accountstatus||account.accountstatus} />} />
                <Row label="Margins" value={fmtDate(accountOriginalFundingDate)} />
                <Row label="Funding Date" value={fmtDate(accountOriginalFundingDate)} />
                <Row label="Maturity Date" value={fmtDate(accountMaxMaturityDate)} />
                <Row label="Avg Risk Rating" value={
                  account.avgriskrating
                    ? <span style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <RiskBadge rating={Number(account.avgriskrating).toFixed(1)} />
                        <span style={{ fontSize:11, color:"var(--color-text-secondary)" }}>avg of {account.ratedloancount} risk rating{account.ratedloancount!==1?"s":""}</span>
                      </span>
                    : "—"
                } />
                <Row label="Manager"  value={account.account_manager_name||"—"} />
                <Row label="Industry" value={account.industryname||"—"} />
                
              </div>
            </div>;
          })()}
        </Card>

      {/* ── StatCard Card ── */}

        <Card style={{ padding:16 }}>
          <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:12, paddingBottom:6, borderBottom:"1px solid "+B.cyanLight }}>Balances</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:8 }}>
            <StatCard label="Commitment" value={fmt(account.accountcommitmentamt||0)} />      
            <StatCard label="Collateral" value={fmt(account.totalcollateralvalue||0)} />  
            <StatCard label="Balance EOD" value={fmt(account.accountbalanceeod||0)} />
            <StatCard label="BS Loan Balance" value={fmt(account.bsloanbalanceeod||0)} />
            <StatCard label="Funded Terms" value={fmt(account.fundedtermfundings||0)} />
            <StatCard label="Funded Draws" value={fmt(account.fundeddraws||0)} />
            <StatCard label="Principal Received" value={fmt(account.receivedprinpmts||0)} />
            <StatCard label="Excess Availability" value={fmt(account.excessavailability||0)} />
          </div>
        </Card>
      </div>

      {/* ── Tabs ── */}
      <div style={{ marginTop:16 }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:3, paddingLeft:0, position:"relative", zIndex:1, marginTop:4, borderBottom:"4px solid "+B.cyan }}>
        {["overview",
          /* UCC, tax, MCA and bank activity — all four registers are for
             portfolio managers and admins, so the tab is too. */
          ...(role.canViewMonitoring ? ["monitoring"] : []),
          "relationships","loans","covenants","collateral","terms","draws","ach-collections","scheduled","rpmts","bankaccounts","legal","statements","notes",
          /* Captured correspondence is more sensitive than the rest of the
             account, so the tab is only offered to Admins and portfolio
             managers. The API enforces the same rule independently. */
          ...(canSeeEmails ? ["emails"] : [])].map(t=>(
          <TabBtn key={t} id={t} current={tab}
            label={t==="scheduled"?"Scheduled Pmts":t==="rpmts"?"Received Pmts":t==="bankaccounts"?"Bank Accounts":t==="ach-collections"?"ACH Collections":t==="legal"?"Legal Events":t==="statements"?"Statements":t.charAt(0).toUpperCase()+t.slice(1)}
            onClick={setTab} />
        ))}
        </div>
        {/* The tab body: a cool blue-grey (#D5E7F0) — darker than the white cards
            so each card reads as its own block, and a different hue from the
            grey page behind it, with a firmer edge and the cards' shadow, so
            the whole panel still stands off the page. */}
        <div style={{ background:"#D5E7F0", border:"1px solid #A9C9D8", boxShadow:"0 2px 12px rgba(0,0,0,0.08)", borderTop:"none", borderRadius:"0 0 8px 8px", padding:16, position:"relative", zIndex:2 }}>

         {tab==="overview"&&(()=>{
            const Row = ({ label, value }) => (
              <div style={{ display:"flex", justifyContent:"space-between", gap:8, fontSize:13, padding:"4px 0" }}>
                <span style={{ color:"var(--color-text-secondary)", flexShrink:0 }}>{label}</span>
                <span style={{ fontWeight:500, textAlign:"right" }}>{value}</span>
              </div>
            );
            const Panel = ({ title, children }) => (
              <Card style={{ padding:16 }}>
                <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:12, paddingBottom:6, borderBottom:"1px solid "+B.cyanLight }}>{title}</div>
                {children}
              </Card>
            );
            return (<>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))", gap:12, alignItems:"start" }}>

                {/* ── Account Details ── */}
                <Panel title="Account Details">
                  <Row label="Account"       value={account.companyname} />
                  <Row label="Account #"     value={<span style={{ fontFamily:"var(--font-mono)" }}>{account.accountnumber||"—"}</span>} />
                  <Row label="Status"        value={account.accountstatus ? <Badge s={account.accountstatus} /> : "—"} />
                  <Row label="Manager"       value={account.account_manager_name||"—"} />
                  <Row label="Industry"      value={account.industryname||"—"} />
                  <Row label="Facility Types" value={account.facilitytypes||"—"} />
                  <Row label="Collateral Types" value={account.collateraltypes||"—"} />
                  <Row label="Avg Risk Rating" value={account.avgriskrating!=null ? Number(account.avgriskrating).toFixed(1)+" ("+account.ratedloancount+")" : "—"} />
                  <Row label="Original Funding Date" value = {fmtDate(accountOriginalFundingDate)} />
                  <Row label="Maturity Date" value={fmtDate(accountMaxMaturityDate)} />
                </Panel>

                {/* ── Balances ── */}
                <Panel title="Balances">
                  <Row label="Commitment"         value={fmt(account.accountcommitmentamt||0)} />
                  <Row label="Collateral"         value={fmt(account.totalcollateralvalue||0)} />
                  <Row label="Balance BOD"        value={fmt(account.accountbalancebod||0)} />
                  <Row label="Current Balance EOD" value={fmt(account.accountbalanceeod||0)} />
                  <Row label="BS Loan Balance EOD" value={fmt(account.bsloanbalanceeod||0)} />
                  <div style={{ borderTop:"1px solid "+B.cyanLight, margin:"7px 0" }} />
                  <Row label="Funded Term Fundings" value={fmt(account.fundedtermfundings||0)} />
                  <Row label="Funded Draws"       value={fmt(account.fundeddraws||0)} />
                  <Row label="Received Principal" value={fmt(account.receivedprinpmts||0)} />
                </Panel>

                {/* ── Collateral ── */}
                <Panel title="Collateral">
                  <div style={{ background:B.cyanLight, borderRadius:6, padding:"8px 10px", marginBottom:8 }}>
                    <div style={{ fontSize:11, color:"var(--color-text-secondary)" }}>Excess Availability</div>
                    <div style={{ fontSize:19, fontWeight:600, color: parseFloat(account.excessavailability)>=0 ? "#0F6E56" : "#993C1D" }}>{fmt(account.excessavailability||0)}</div>
                  </div>
                  <Row label="Total Collateral Value" value={fmt(account.totalcollateralvalue||0)} />
                </Panel>

                {/* ── Payment Status ── */}
                <Panel title="Payment Status">
                  <Row label="Next Sched Date"      value={account.nextscheddate ? fmtDate(account.nextscheddate) : "—"} />
                  <Row label="Next Sched Payment"   value={fmt(account.nextschedamount||0)} />
                  <Row label="Total Sched Pmts Due" value={fmt(account.totalscheddue||0)} />
                  <Row label="Days Delinquent"      value={account.daysdelinquent ?? 0} />
                  <Row label="Delinquency Status"   value={account.delinquencystatus ? <Badge s={account.delinquencystatus} /> : "—"} />
                </Panel>

              </div>

              {/* ── Monitoring: the headline from each register, scoped to this
                  borrower. Details → opens the Monitoring tab. */}
              {role.canViewMonitoring && <MonitoringSummary accountid={account.id} onOpen={() => setTab("monitoring")} />}
            </>);
          })()}

       {tab==="monitoring" && role.canViewMonitoring && <MonitoringTab accountid={account.id} />}

       {tab==="relationships"&&(()=>{
        const SectionTitle = ({ title, action }) => (
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, paddingBottom:6, borderBottom:"1px solid "+B.cyanLight }}>
            <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em" }}>{title}</div>
            {action}
          </div>
        );

        return <>
          {/* ── Contacts ── title inside the card, like the Overview panels */}
          <Card style={{ marginBottom:16, overflowX:"auto" }}>
            <SectionTitle title="Contacts" action={
              role.canEdit&&<Btn onClick={()=>setModal({type:"contact-add",accountid:account?.id,onDone:load})} variant="primary">+ Add Contact</Btn>
            } />
              {contacts.length===0
                ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:12 }}>No contacts recorded.</div>
                : <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>
                      <STh label="Title" sortKey="title" currentKey={ctSK} currentDir={ctSD} onSort={ctToggle} />
                      <STh label="Name" sortKey="fullname" currentKey={ctSK} currentDir={ctSD} onSort={ctToggle} />
                      <STh label="Email" sortKey="email" currentKey={ctSK} currentDir={ctSD} onSort={ctToggle} />
                      <STh label="Phone" sortKey="phone" currentKey={ctSK} currentDir={ctSD} onSort={ctToggle} />
                      <STh label="Mobile" sortKey="mobilephone" currentKey={ctSK} currentDir={ctSD} onSort={ctToggle} />
                      <STh label="Invoice Delivery" sortKey="invoicedelivery" currentKey={ctSK} currentDir={ctSD} onSort={ctToggle} />
                      <STh label="Portal" sortKey="_portal" currentKey={ctSK} currentDir={ctSD} onSort={ctToggle} />
                      {/* News Monitor: the contact's "Monitor news" box. Portfolio managers and admins only, like the page. */}
                      {role.canViewMonitoring && <STh label="News" sortKey="monitornews" currentKey={ctSK} currentDir={ctSD} onSort={ctToggle} />}<Th></Th>
                    </tr></thead>
                    <tbody>{sortedContacts.map(c=>(
                      <tr key={c.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"contact-view",rec:{...c,id:c.id,accountid:c.accountid}})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <Td>{c.title||"—"}</Td>
                        <Td style={{ fontWeight:500 }}>{c.fullname}</Td>
                        <Td>{c.email||"—"}</Td>
                        <Td>{fmtPhone(c.phone)||"—"}</Td>
                        <Td>{fmtPhone(c.mobilephone)||"—"}</Td>
                        <Td>{c.invoicedelivery?"✓":"—"}</Td>
                        {/* Was a tick on the legacy flag alone, which said what
                            somebody intended rather than whether the person can
                            actually sign in. Both facts now read through one
                            helper — see src/shared/portalAccess.js. */}
                        <Td><PortalBadge contact={c} /></Td>
                        {role.canViewMonitoring && <Td>{c.monitornews ? <span title="Searched weekly on News Monitor" style={{ fontSize:11, fontWeight:600, padding:"2px 8px", borderRadius:10, background:"#E6F7FC", color:B.cyanDark, whiteSpace:"nowrap" }}>Monitored</span> : "—"}</Td>}
                        <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                          <Btn onClick={()=>setModal({type:"contact-view",rec:{...c,id:c.id,accountid:c.accountid}})}><Icon type="view" /></Btn>
                          {role.canEdit&&<Btn onClick={()=>setModal({type:"contact-edit",rec:{...c,id:c.id,accountid:c.accountid},onDone:load})}><Icon type="edit" /></Btn>}
                          {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete this contact?",onConfirm:()=>delRec("Contacts",c.id,"Deleted contact",load)})} variant="danger"><Icon type="delete" /></Btn>}
                        </div></Td>
                      </tr>
                    ))}</tbody>
                  </table>}
          </Card>

          {/* ── Borrowers/Guarantors ── */}
          <Card style={{ overflowX:"auto" }}>
            <SectionTitle title="Borrowers/Guarantors" action={
              role.canEdit&&<Btn onClick={()=>setModal({type:"borrowerguarantor-add",accountid:account?.id,onDone:load})} variant="primary">+ Add Borrower/Guarantor</Btn>
            } />
              {borrowersGuarantors.length===0
                ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:12 }}>No borrowers or guarantors recorded.</div>
                : <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>
                      <STh label="Name" sortKey="borrowerguarantorname" currentKey={bgSK} currentDir={bgSD} onSort={bgToggle} />
                      <STh label="Role" sortKey="borrowerguarantor" currentKey={bgSK} currentDir={bgSD} onSort={bgToggle} />
                      <STh label="Entity Type" sortKey="entitytype" currentKey={bgSK} currentDir={bgSD} onSort={bgToggle} />
                      <STh label="State of Org" sortKey="stateoforganization" currentKey={bgSK} currentDir={bgSD} onSort={bgToggle} />
                      {uccFilings && <STh label="UCC Status" sortKey="_ucc" currentKey={bgSK} currentDir={bgSD} onSort={bgToggle} />}
                      <STh label="TIN" sortKey="tinlast4" currentKey={bgSK} currentDir={bgSD} onSort={bgToggle} />
                      <STh label="Bank Accounts" sortKey="_banks" currentKey={bgSK} currentDir={bgSD} onSort={bgToggle} numeric />
                      <Th></Th>
                    </tr></thead>
                    <tbody>{sortedBG.map(bg=>{
                      const baCount = bankAccounts.filter(b=>b.borrowerid===bg.id).length;
                      return (
                        <tr key={bg.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"borrowerguarantor-view",rec:{...bg,id:bg.id,accountid:bg.accountid}})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                          <Td style={{ fontWeight:500 }}>{bg.borrowerguarantorname||"—"}</Td>
                          <Td><Badge s={bg.borrowerguarantor} /></Td>
                          <Td>{bg.entitytype||"—"}</Td>
                          {/* Where the entity is REGISTERED, which decides the
                              filing office a UCC-1 has to go to. Amber means the
                              value was copied from the account's headquarters
                              state and nobody has checked it against the
                              charter yet. */}
                          <Td><StateOfOrgBadge entity={bg} /></Td>
                          {uccFilings && (() => {
                            const u = uccStatusFor(bg, uccFilings);
                            return <Td title={u.tip} onClick={e => { if (u.filings.length) { e.stopPropagation(); setTab("monitoring"); } }}>
                              <span style={{ fontSize:12, fontWeight:600, padding:"2px 8px", borderRadius:4,
                                             background:u.bg, color:u.fg, whiteSpace:"nowrap" }}>{u.label}</span>
                            </Td>;
                          })()}
                          {/* Last four only, computed on the server. The full
                              TIN is no longer sent to the browser, so there is
                              nothing here to mask. */}
                          <Td><span style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{bg.tinlast4 ? "••••"+bg.tinlast4 : "—"}</span></Td>
                          <Td>{baCount||"—"}</Td>
                          <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                            <Btn onClick={()=>setModal({type:"borrowerguarantor-view",rec:{...bg,id:bg.id,accountid:bg.accountid}})}><Icon type="view" /></Btn>
                            {role.canEdit&&<Btn onClick={()=>setModal({type:"borrowerguarantor-edit",rec:{...bg,id:bg.id,accountid:bg.accountid},onDone:load})}><Icon type="edit" /></Btn>}
                            {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete this borrower/guarantor?",onConfirm:()=>delRec("BorrowersGuarantors",bg.id,"Deleted borrower/guarantor",load)})} variant="danger"><Icon type="delete" /></Btn>}
                          </div></Td>
                        </tr>
                      );
                    })}</tbody>
                  </table>}
          </Card>
        </>;
      })()}

          {/* ── Loans ── */}
          {tab==="loans"&&<>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, flexWrap:"wrap", gap:8 }}>
              <SearchBar value={qLoans} onChange={setQLoans} placeholder="Search loans..." />
              <div style={{ display:"flex", gap:8 }}>
                <Btn onClick={()=>exportCSV(filtLoans,["loannumber","originalfundingdate","maturitydate","collateraltype","facilitytype","loanstatus","commitmentamount","loanbalancebod","loanbalanceeod","bsloanbalanceeod"],"loans.csv")}>Export CSV</Btn>
                {role.canEdit&&<Btn onClick={()=>setModal({type:"loan-add",accountid:account?.id,onDone:load})} variant="primary">+ Add Loan</Btn>}
              </div>
            </div>

            {(()=>{
              const loanGroupOrder = ["Accrual","Non Accrual","Charged Off", "Funding in Progress","Terminated","Paid Off"];
              const loanGroups = loanGroupOrder.map(status=>({
                status,
                rows: sortedLoans.filter(l=>l.loanstatus===status)
              })).filter(g=>g.rows.length>0);

              const loanTotals = arr => ({
                count:      arr.length,
                commitment: arr.reduce((s,l)=>s+(parseFloat(l.commitmentamount)||0),0),
                bod:        arr.reduce((s,l)=>s+(parseFloat(l.loanbalancebod)||0),0),
                collateral: arr.reduce((s,l)=>s+(parseFloat(l.ea_totalcollateralvalue)||0),0),
                eod:        arr.reduce((s,l)=>s+(parseFloat(l.loanbalanceeod)||0),0),
                aeq:        arr.reduce((s,l)=>s+(parseFloat(l.bsloanbalanceeod)||0),0),
                excessavail:        arr.reduce((s,l)=>s+(parseFloat(l.excessavailability)||0),0),
              });
              const grand = loanTotals(filtLoans);
              const COLS = 16;   // 15 data columns + the actions column (so header highlight spans full width)

              return <Card style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
                 <thead><tr>
                    <STh label="Account #"       sortKey="accountnumber"      currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} />
                    <STh label="Loan #"          sortKey="loannumber"         currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} />
                    <STh label="Company Name"    sortKey="companyname"        currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} />
                    <STh label="State"           sortKey="state"              currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} />
                    <STh label="Collateral Type" sortKey="collateraltype"     currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} />
                    <STh label="Status"          sortKey="loanstatus"         currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} />
                    <STh label="Facility Type"   sortKey="facilitytype"       currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} />
                    <STh label="Orig Date"       sortKey="originalfundingdate" currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} />
                    <STh label="Maturity Date"   sortKey="maturitydate"       currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} />
                    <STh label="Margin"          sortKey="margin"             currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} numeric />
                    <STh label="Commitment Amt"  sortKey="commitmentamount"   currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} numeric />
                    <STh label="Collateral"      sortKey="ea_totalcollateralvalue"   currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} numeric />                    
                    <STh label="Loan Balance" sortKey="loanbalanceeod"    currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} numeric />
                    <STh label="BS Loan Balance" sortKey="bsloanbalanceeod" currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} numeric />
                    <STh label="Excess Avail" sortKey="excessavailability" currentKey={loanSortKey} currentDir={loanSortDir} onSort={loanToggle} numeric />
                    <Th></Th>
                  </tr></thead>
                  <tbody>
                    {loanGroups.map(group=>{
                      const sub = loanTotals(group.rows);
                      return <>
                        {/* ── Group header ── */}
                        <tr key={"hdr-"+group.status}>
                          <td colSpan={COLS} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
                            <Badge s={group.status} />
                            <span style={{ marginLeft:8, fontSize:11, color:B.cyanDark, fontWeight:400 }}>{sub.count} loan{sub.count!==1?"s":""}</span>
                          </td>
                        </tr>

                        {/* ── Group rows ── */}
                        {group.rows.map(l=>{
                          const lp = {accountid:account.id,loanid:l.id,companyname:account.companyname,loannumber:l.loannumber};
                          // Full-cell anchor: left-click navigates in-app; right/middle-click get the
                          // browser's native "Open in new tab / window" menu. Negative margin fills the cell.
                          const A = (children, extra) => <PageLink page="loan" params={lp} style={{ display:"block", margin:"-9px -12px", padding:"9px 12px", ...extra }}>{children}</PageLink>;
                          return (
                          <tr key={l.id} style={{ cursor:"pointer" }} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                            <Td>{A(account.accountnumber||"—")}</Td>
                            <Td>{A(l.loannumber, { fontFamily:"var(--font-mono)", fontSize:12 })}</Td>
                            <Td>{A(l.companyname||"—")}</Td>
                            <Td>{A(account.state||"—")}</Td>
                            <Td>{A(l.collateraltype||"—")}</Td>
                            <Td>{A(<Badge s={l.loanstatus} />)}</Td>
                            <Td>{A(l.facilitytype||"—")}</Td>
                            <Td>{A(fmtDate(l.originalfundingdate))}</Td>
                            <Td>{A(fmtDate(l.maturitydate))}</Td>
                            <Td>{A(l.margin?fmtRatio(l.margin):"—")}</Td>
                            <TdNum>{A(fmt(l.commitmentamount||0), { textAlign:"right" })}</TdNum>
                            <TdNum>{A(fmt(l.ea_totalcollateralvalue||0), { textAlign:"right" })}</TdNum>
                            <TdNum>{A(fmt(l.loanbalanceeod||0), { textAlign:"right" })}</TdNum>
                            <TdNum>{A(fmt(l.bsloanbalanceeod||0), { textAlign:"right" })}</TdNum>
                            <TdNum style={{ color: parseFloat(l.excessavailability||0) >= 0 ? 'inherit' : '#993C1D' }}>{A(fmt(l.excessavailability||0), { textAlign:"right" })}</TdNum>

                            <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                              <Btn onClick={()=>go("loan",{accountid:account.id,loanid:l.id,companyname:account.companyname,loannumber:l.loannumber})}><Icon type="view" /></Btn>
                              {role.canEdit&&<Btn onClick={()=>setModal({type:"loan-edit",rec:{...l,id:l.id,accountid:l.accountid,loannumber:l.loannumber,loanbalanceeod:l.loanbalanceeod,originalfundingdate:l.originalfundingdate?.slice(0,10),maturitydate:l.maturitydate?.slice(0,10),loanstatus:l.loanstatus,facilitytype:l.facilitytype},onDone:load})}><Icon type="edit" /></Btn>}
                              {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete loan "+l.loannumber+"?",onConfirm:()=>delRec("Loans",l.id,"Deleted loan "+l.loannumber,load)})} variant="danger"><Icon type="delete" /></Btn>}
                            </div></Td>
                          </tr>
                          );
                        })}

                        {/* ── Group subtotal ── */}
                        <tr key={"sub-"+group.status} style={{ background:"var(--color-background-secondary)", borderTop:"1.5px solid "+B.cyanLight }}>
                          <td colSpan={10} style={{ padding:"8px 12px", fontSize:12, fontWeight:600, color:B.cyanDark, textAlign:"right" }}>
                            {group.status} Subtotal ({sub.count})
                          </td>
                          <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.commitment)}</TdNum>
                          <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.collateral)}</TdNum>
                          <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.eod)}</TdNum>
                          <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.aeq)}</TdNum>
                          <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.excessavail)}</TdNum>
                          <td />
                        </tr>
                      </>;
                    })}

                    {/* ── Grand total ── */}
                    
                    <tr style={{ background:B.cyanLight, borderTop:"2px solid "+B.cyan }}>
                      <td colSpan={10} style={{ padding:"10px 12px", fontSize:13, fontWeight:700, color:B.cyanDark, textAlign:"right" }}>
                        Grand Total ({grand.count} loan{grand.count!==1?"s":""})
                      </td>
                      <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.commitment)}</TdNum>
                      <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.collateral)}</TdNum>
                      <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.eod)}</TdNum>
                      <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.aeq)}</TdNum>
                      <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.excessavail)}</TdNum>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </Card>;
            })()}
          </>}
          {tab==="covenants"&&(()=>{
            const fundingDates  = loans.map(l=>l.originalfundingdate).filter(Boolean).sort();
            const maturityDates = loans.map(l=>l.maturitydate).filter(Boolean).sort();
            return <CovenantTab
              accountid={account.id}
              accountFundingDate={fundingDates[0]||null}
              accountMaturityDate={maturityDates[maturityDates.length-1]||null}
              canEdit={role.canEdit} canDelete={role.canDelete} />;
          })()}

          {tab === "collateral" && (
            <div>
              {loans.filter(l => !['Terminated'].includes(l.loanstatus)).map(loan => (
                <div key={loan.id} style={{ marginBottom: 24 }}>
                  {/* The loan's name goes INSIDE its cards (the summary band and
                      Entry History's title), not as a heading above them. */}
                  <CollateralTab
                    title={`${loan.loannumber}${loan.facilitytype ? " — " + loan.facilitytype : ""}`}
                    loanid={loan.id}
                    canEdit={role.canEdit}
                    canDelete={role.canDelete}
                  />
                </div>
              ))}
            </div>
          )}
          {/* ── Terms ── */}
          {tab==="terms"&&<>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, flexWrap:"wrap", gap:8 }}>
              <SearchBar value={qTerms} onChange={setQTerms} placeholder="Search terms..." />
              <div style={{ display:"flex", gap:8 }}>
                <Btn onClick={()=>exportCSV(filtTerms,["loannumber","facility_type","term_type","commitment","fundingamount","margin","fundingdate","pipelinestage"],"terms.csv")}>Export CSV</Btn>
              </div>
            </div>
            <Card><table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>
                <STh label="Loan #"         sortKey="loannumber"          currentKey={termSortKey} currentDir={termSortDir} onSort={termToggle} />
                <STh label="Pipeline Stage" sortKey="pipelinestage"       currentKey={termSortKey} currentDir={termSortDir} onSort={termToggle} />
                <STh label="Company Name"   sortKey="companyname"         currentKey={termSortKey} currentDir={termSortDir} onSort={termToggle} />
                <STh label="Term Type"      sortKey="term_type"           currentKey={termSortKey} currentDir={termSortDir} onSort={termToggle} />
                <STh label="Facility Type"  sortKey="facility_type"       currentKey={termSortKey} currentDir={termSortDir} onSort={termToggle} />
                <STh label="Index Name"     sortKey="rateindex"           currentKey={termSortKey} currentDir={termSortDir} onSort={termToggle} />
                <STh label="Margin"         sortKey="margin"              currentKey={termSortKey} currentDir={termSortDir} onSort={termToggle} numeric />
                <STh label="Commitment"     sortKey="commitment"          currentKey={termSortKey} currentDir={termSortDir} onSort={termToggle} numeric />
                <STh label="New Commitment" sortKey="newcommitmentamount" currentKey={termSortKey} currentDir={termSortDir} onSort={termToggle} numeric />
                <STh label="Funding Date"   sortKey="fundingdate"         currentKey={termSortKey} currentDir={termSortDir} onSort={termToggle} />
                <STh label="Funding Amt"    sortKey="fundingamount"       currentKey={termSortKey} currentDir={termSortDir} onSort={termToggle} numeric />
                <STh label="Maturity Date"  sortKey="maturitydate"        currentKey={termSortKey} currentDir={termSortDir} onSort={termToggle} />

                <Th></Th></tr></thead>
              <tbody>
              {loans.map(loan => {
                const loanTerms = sortedTerms.filter(t => t.loanid === loan.id);
                if (loanTerms.length === 0) return null;
                // A bare <> cannot carry a key, so every loan band was an
                // unkeyed sibling in the same list — React warns, and reorders
                // on sort reuse the wrong rows' state.
                return <Fragment key={"grp-"+loan.id}>
                  <tr key={"hdr-"+loan.id}>
                    <td colSpan={13} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
                      {loan.loannumber} — {loan.facilitytype}  
                    </td>
                  </tr>
                  {loanTerms.map(t=>(
                    <tr key={t.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"terms-view",rec:{...t,id:t.id,loanid:t.loanid}})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                      <Td><span style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{t.loannumber}</span></Td>
                      <Td><Badge s={t.pipelinestage} /></Td>
                      <Td>{t.companyname}</Td>
                      <Td>{t.term_type}</Td>
                      <Td>{t.facility_type}</Td>
                      <Td>{t.rateindex}</Td>                     
                      <TdNum>{fmtRatio(t.margin)}</TdNum>
                      <TdNum>{fmt(t.commitment)}</TdNum>
                      <TdNum>{fmt(t.newcommitmentamount)}</TdNum>
                      <Td>{fmtDate(t.fundingdate)}</Td>
                      <TdNum>{fmt(t.fundingamount)}</TdNum>
                      <Td>{fmtDate(t.maturitydate)}</Td>
                      <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                        <Btn onClick={()=>setModal({type:"terms-view",rec:{...t,id:t.id,loanid:t.loanid}})}><Icon type="view" /></Btn>
                        {role.canEdit&&<Btn onClick={()=>setModal({type:"terms-edit",rec:{...t,id:t.id,loanid:t.loanid},onDone:load})}><Icon type="edit" /></Btn>}
                        {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete these terms?",onConfirm:()=>delRec("Terms",t.id,"Deleted terms",load)})} variant="danger"><Icon type="delete" /></Btn>}
                      </div></Td>
                    </tr>
                  ))}
                </Fragment>;
              })}
            </tbody>
            </table></Card>

            <AccountChecklistCards accountId={view.accountid} />
          </>}

          {/* ── ACH Collections ── */}
          {tab==="ach-collections"&&<>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, flexWrap:"wrap", gap:8 }}>
              <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>{achCollections.length} record{achCollections.length!==1?"s":""}</div>
              <div style={{ display:"flex", gap:8 }}>
                <Btn onClick={()=>exportCSV(sortedAch,["loannumber","bank_acct_number","underwriterName","requested_ach_date","expected_ach_processed_date","ach_requested_amt","setUpByName","ach_collection_set_up_date","ach_set_up_amount","fund_ownership"],"ach_collections.csv")}>Export CSV</Btn>
                {role.canEdit&&<Btn onClick={()=>setModal({type:"ach-add",accountid:account.id,onDone:load})} variant="primary">+ Add ACH Collection</Btn>}
              </div>
            </div>
            <Card style={{ overflowX:"auto" }}>
              {achCollections.length===0
                ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:12 }}>No ACH collections recorded.</div>
                : <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
                    <thead><tr>
                      <STh label="Loan #"        sortKey="loannumber"                  currentKey={achSortKey} currentDir={achSortDir} onSort={achToggle} />
                      <STh label="Bank Acct"     sortKey="bank_acct_number"            currentKey={achSortKey} currentDir={achSortDir} onSort={achToggle} />
                      <STh label="Underwriter"   sortKey="underwriterName"             currentKey={achSortKey} currentDir={achSortDir} onSort={achToggle} />
                      <STh label="Req ACH Date"  sortKey="requested_ach_date"          currentKey={achSortKey} currentDir={achSortDir} onSort={achToggle} />
                      <STh label="Exp Processed" sortKey="expected_ach_processed_date" currentKey={achSortKey} currentDir={achSortDir} onSort={achToggle} />
                      <STh label="Req Amt"       sortKey="ach_requested_amt"           currentKey={achSortKey} currentDir={achSortDir} onSort={achToggle} numeric />
                      <STh label="Set Up By"     sortKey="setUpByName"                 currentKey={achSortKey} currentDir={achSortDir} onSort={achToggle} />
                      <STh label="Set Up Date"   sortKey="ach_collection_set_up_date"  currentKey={achSortKey} currentDir={achSortDir} onSort={achToggle} />
                      <STh label="Set Up Amt"    sortKey="ach_set_up_amount"           currentKey={achSortKey} currentDir={achSortDir} onSort={achToggle} numeric />
                      <STh label="Fund Ownership" sortKey="fund_ownership"             currentKey={achSortKey} currentDir={achSortDir} onSort={achToggle} />
                      <Th></Th>
                    </tr></thead>
                    <tbody>{sortedAch.map(x=>(
                      <tr key={x.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"ach-view",rec:{...x,id:x.id,loanid:x.loanid},accountid:account.id})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <Td><span style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{x.loannumber}</span></Td>
                        <Td>{x.bank_acct_number?("••••"+x.bank_acct_number):"—"}</Td>
                        <Td>{x.underwriterName||"—"}</Td>
                        <Td>{fmtDate(x.requested_ach_date)}</Td>
                        <Td>{fmtDate(x.expected_ach_processed_date)}</Td>
                        <TdNum style={{ fontWeight:500 }}>{fmt(x.ach_requested_amt)}</TdNum>
                        <Td>{x.setUpByName||"—"}</Td>
                        <Td>{x.ach_collection_set_up_date?fmtDate(x.ach_collection_set_up_date):"—"}</Td>
                        <TdNum>{x.ach_set_up_amount!=null?fmt(x.ach_set_up_amount):"—"}</TdNum>
                        <Td>{x.fund_ownership||"—"}</Td>
                        <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                          <Btn onClick={()=>setModal({type:"ach-view",rec:{...x,id:x.id,loanid:x.loanid},accountid:account.id})}><Icon type="view" /></Btn>
                          {role.canEdit&&<Btn onClick={()=>setModal({type:"ach-edit",rec:{...x,id:x.id,loanid:x.loanid},accountid:account.id,onDone:load})}><Icon type="edit" /></Btn>}
                          {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete this ACH collection?",onConfirm:()=>delRec("ACH_Collection",x.id,"Deleted ACH collection",load)})} variant="danger"><Icon type="delete" /></Btn>}
                        </div></Td>
                      </tr>
                    ))}</tbody>
                  </table>}
            </Card>
          </>}

          {/* ── Draws ── */}
          {tab==="draws"&&<>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, flexWrap:"wrap", gap:8 }}>
              <SearchBar value={qDraws} onChange={v=>{ setQDraws(v); resetDrawPage(); }} placeholder="Search draws..." />
              <div style={{ display:"flex", gap:8 }}>
                <Btn onClick={()=>exportCSV(sortedDraws,["loannumber","drawrequestedbyName","expectedfunddate","expecteddrawamount","drawfundedbyName","fundeddate","fundingamount"],"draws.csv")}>Export CSV</Btn>
              </div>
            </div>

            {(()=>{
              // Group by loannumber in loan order
            const drawTotalsCalc = arr => ({
              count:         arr.length,
              expecteddraw:  arr.reduce((s,d)=>s+(parseFloat(d.expecteddrawamount)||0),0),
              fundingamount: arr.reduce((s,d)=>s+(parseFloat(d.fundingamount)||0),0),
            });
            const drawGrand = drawTotalsCalc(sortedDraws);

            // Groups built from displayedDraws (paginated slice)
            const drawLoanNumbers = [...new Set(displayedDraws.map(d=>d.loannumber))];
            const drawGroups = drawLoanNumbers.map(loannumber=>({
              loannumber,
              rows: displayedDraws.filter(d=>d.loannumber===loannumber)
            })).filter(g=>g.rows.length>0);


              
              return <Card style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
                  <thead><tr>
                    <STh label="Account #"        sortKey="accountnumber"          currentKey={drawSortKey} currentDir={drawSortDir} onSort={drawToggle} />
                    <STh label="Loan #"        sortKey="loannumber"          currentKey={drawSortKey} currentDir={drawSortDir} onSort={drawToggle} />
                    <STh label="Company Name By"  sortKey="companyname" currentKey={drawSortKey} currentDir={drawSortDir} onSort={drawToggle} />
                    <STh label="Requested By"  sortKey="drawrequestedbyName" currentKey={drawSortKey} currentDir={drawSortDir} onSort={drawToggle} />
                    <STh label="Exp Fund Date" sortKey="expectedfunddate"    currentKey={drawSortKey} currentDir={drawSortDir} onSort={drawToggle} />
                    <STh label="Exp Draw Amt"  sortKey="expecteddrawamount"  currentKey={drawSortKey} currentDir={drawSortDir} onSort={drawToggle} numeric />
                    <STh label="Funded By"     sortKey="drawfundedbyName"    currentKey={drawSortKey} currentDir={drawSortDir} onSort={drawToggle} />
                    <STh label="Funded Date"   sortKey="fundeddate"          currentKey={drawSortKey} currentDir={drawSortDir} onSort={drawToggle} />
                    <STh label="Funding Amt"   sortKey="fundingamount"       currentKey={drawSortKey} currentDir={drawSortDir} onSort={drawToggle} numeric />
                    <STh label="Same-Day Exempt" sortKey="samedayinterestexemption" currentKey={drawSortKey} currentDir={drawSortDir} onSort={drawToggle} />
                    <STh label="Capitalized"   sortKey="capitalized"          currentKey={drawSortKey} currentDir={drawSortDir} onSort={drawToggle} />
                    <Th></Th>
                  </tr></thead>
                  <tbody>
                    {drawGroups.map(group=>{
                      const sub = drawTotalsCalc(group.rows);
                      // Only show rows for current page within this group
                      const loan = loans.find(l=>l.loannumber===group.loannumber);
                      return <>
                        {/* ── Group header ── */}
                        <tr key={"hdr-"+group.loannumber}>
                          <td colSpan={99} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
                            {group.loannumber}
                            {loan&&<> — {loan.facilitytype} — <Badge s={loan.loanstatus} /></>}
                            <span style={{ marginLeft:8, fontSize:11, color:B.cyanDark, fontWeight:400 }}>{sub.count} draw{sub.count!==1?"s":""}</span>
                          </td>
                        </tr>

                        {/* ── Group rows ── */}
                        {group.rows.map(d=>(
                          <tr key={d.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"draw-view",rec:{...d,id:d.id,loanid:d.loanid,expectedfunddate:d.expectedfunddate,fundingamount:d.fundingamount,fundeddate:d.fundeddate,expecteddrawamount:d.expecteddrawamount,drawfundedby:d.drawfundedby,drawrequestedby:d.drawrequestedby}})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                            <Td><span style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{account.accountnumber}</span></Td>
                            <Td><span style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{d.loannumber}</span></Td>
                            <Td>{d.companyname}</Td>
                            <Td>{d.drawrequestedbyName||"—"}</Td>
                            <Td>{fmtDate(d.expectedfunddate)}</Td>
                            <TdNum>{fmt(d.expecteddrawamount||0)}</TdNum>
                            <Td>{d.drawfundedbyName||"—"}</Td>
                            <Td>{fmtDate(d.fundeddate)}</Td>
                            <TdNum style={{ fontWeight:500 }}>{fmt(d.fundingamount||0)}</TdNum>
                            <Td style={{ textAlign:"center" }}>
                              {d.samedayinterestexemption ? <span style={{ color:"var(--color-cyan-dark)", fontWeight:600, fontSize:11 }}>Yes</span> : <span style={{ color:"var(--color-text-muted)", fontSize:11 }}>—</span>}
                            </Td>
                            <Td style={{ textAlign:"center" }}>
                              {d.capitalized ? <span style={{ color:"var(--color-cyan-dark)", fontWeight:600, fontSize:11 }}>Yes</span> : <span style={{ color:"var(--color-text-muted)", fontSize:11 }}>—</span>}
                            </Td>
                            <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                              <Btn onClick={()=>setModal({type:"draw-view",rec:{...d,id:d.id,loanid:d.loanid,expectedfunddate:d.expectedfunddate,fundingamount:d.fundingamount,fundeddate:d.fundeddate,expecteddrawamount:d.expecteddrawamount,drawfundedby:d.drawfundedby,drawrequestedby:d.drawrequestedby}})}><Icon type="view" /></Btn>
                              {role.canEdit&&<Btn onClick={()=>setModal({type:"draw-edit",rec:{...d,id:d.id,loanid:d.loanid,expectedfunddate:d.expectedfunddate,fundingamount:d.fundingamount,fundeddate:d.fundeddate,expecteddrawamount:d.expecteddrawamount,drawfundedby:d.drawfundedby,drawrequestedby:d.drawrequestedby},onDone:load})}><Icon type="edit" /></Btn>}
                              {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete this draw?",onConfirm:()=>delRec("Draws",d.id,"Deleted draw",load)})} variant="danger"><Icon type="delete" /></Btn>}
                            </div></Td>
                          </tr>
                        ))}

                        {/* ── Group subtotal ── */}
                        <tr key={"sub-"+group.loannumber} style={{ background:"var(--color-background-secondary)", borderTop:"1.5px solid "+B.cyanLight }}>
                          <td colSpan={5} style={{ padding:"8px 12px", fontSize:12, fontWeight:600, color:B.cyanDark, textAlign:"right" }}>
                            {group.loannumber} Subtotal ({sub.count})
                          </td>
                          <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.expecteddraw)}</TdNum>
                          <td colSpan={2} />
                          <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.fundingamount)}</TdNum>
                          <td colSpan={3} />
                        </tr>
                      </>;
                    })}

                    {/* ── Grand total ── */}
                    <tr style={{ background:B.cyanLight, borderTop:"2px solid "+B.cyan }}>
                      <td colSpan={5} style={{ padding:"10px 12px", fontSize:13, fontWeight:700, color:B.cyanDark, textAlign:"right" }}>
                        Grand Total ({drawGrand.count} draw{drawGrand.count!==1?"s":""})
                      </td>
                      <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(drawGrand.expecteddraw)}</TdNum>
                      <td colSpan={2} />
                      <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(drawGrand.fundingamount)}</TdNum>
                      <td colSpan={3} />
                    </tr>
                  </tbody>
                </table>
                <Pagination page={drawPage} totalPages={drawTotalPages} total={drawTotal} pageSize={50} onPage={setDrawPage} />
              </Card>;
            })()}
          </>}

            {/* ── Scheduled Pmts grouped by Loan then Scheduled/Unscheduled ── */}
            {tab==="scheduled"&&<>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, flexWrap:"wrap", gap:8 }}>
                <SearchBar value={qSpmts} onChange={setQSpmts} placeholder="Search scheduled payments..." />
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  {spErr && <span style={{ color:"#993C1D", fontSize:12 }}>{spErr}</span>}
                  {spEdit ? <>
                    <span style={{ fontSize:12, color:"var(--color-text-secondary)" }}>
                      {spmts.filter(spRowChanged).length} changed
                    </span>
                    <Btn onClick={cancelSpEdit} disabled={spBusy}>Cancel</Btn>
                    <Btn onClick={saveSpGrid} disabled={spBusy} variant="primary">{spBusy ? "Saving…" : "Save Changes"}</Btn>
                  </> : <>
                    {role.isAdmin && <Btn onClick={startSpEdit}>Edit Grid</Btn>}
                    <Btn onClick={()=>exportCSV(filtSpmts,["loannumber","unscheduledpayment","scheduleddate","status","scheduledprincipal","scheduledinterest","feetype","scheduledfee","totalscheduledpayment","totalreceivedpmt"],"scheduled_pmts.csv",{ unscheduledpayment: v=>v?"Unscheduled":"Scheduled" })}>Export CSV</Btn>
                  </>}
                </div>
              </div>
              <Card style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
                  <thead><tr>
                    <STh label="Loan #" sortKey="loannumber" currentKey={spSK} currentDir={spSD} onSort={spToggle} />
                    <STh label="Type" sortKey="unscheduledpayment" currentKey={spSK} currentDir={spSD} onSort={spToggle} />
                    <STh label="Sched. Date" sortKey="scheduleddate" currentKey={spSK} currentDir={spSD} onSort={spToggle} />
                    <STh label="Status" sortKey="status" currentKey={spSK} currentDir={spSD} onSort={spToggle} />
                    <STh label="Principal" sortKey="scheduledprincipal" currentKey={spSK} currentDir={spSD} onSort={spToggle} numeric />
                    <STh label="Interest" sortKey="scheduledinterest" currentKey={spSK} currentDir={spSD} onSort={spToggle} numeric />
                    <STh label="Fee Type" sortKey="feetype" currentKey={spSK} currentDir={spSD} onSort={spToggle} />
                    <STh label="Fee" sortKey="scheduledfee" currentKey={spSK} currentDir={spSD} onSort={spToggle} numeric />
                    <STh label="Other Type" sortKey="othertype" currentKey={spSK} currentDir={spSD} onSort={spToggle} />
                    <STh label="Other Fee" sortKey="scheduledother" currentKey={spSK} currentDir={spSD} onSort={spToggle} numeric />
                    <STh label="Pass Thru" sortKey="scheduledpassthrough" currentKey={spSK} currentDir={spSD} onSort={spToggle} numeric />
                    <STh label="Total Sched" sortKey="totalscheduledpayment" currentKey={spSK} currentDir={spSD} onSort={spToggle} numeric />
                    <STh label="Total Received" sortKey="totalreceivedpmt" currentKey={spSK} currentDir={spSD} onSort={spToggle} numeric />
                    <Th></Th>
                  </tr></thead>
                  <tbody>
                    {(()=>{
                      // Grand totals across all loans
                      const spTotals = arr => ({
                        count:      arr.length,
                        principal:  arr.reduce((s,r)=>s+(parseFloat(spv(r,'scheduledprincipal'))||0),0),
                        interest:   arr.reduce((s,r)=>s+(parseFloat(spv(r,'scheduledinterest'))||0),0),
                        fee:        arr.reduce((s,r)=>s+(parseFloat(spv(r,'scheduledfee'))||0),0),
                        other:      arr.reduce((s,r)=>s+(parseFloat(spv(r,'scheduledother'))||0),0),
                        passthru:   arr.reduce((s,r)=>s+(parseFloat(spv(r,'scheduledpassthrough'))||0),0),
                        total:      arr.reduce((s,r)=>s+(spEdit ? spLiveTotal(r) : (parseFloat(r.totalscheduledpayment)||0)),0),
                        received:   arr.reduce((s,r)=>s+(parseFloat(r.totalreceivedpmt)||0),0),
                      });

                      const SubtotalRow = ({ label, data, indent=false, bold=false, bg="var(--color-background-secondary)", borderColor=B.cyanLight }) => (
                        <tr style={{ background:bg, borderTop:"1.5px solid "+borderColor }}>
                          <td colSpan={4} style={{ padding:"7px 12px", fontSize:12, fontWeight:bold?700:600, color:B.cyanDark, textAlign:"right", paddingLeft:indent?32:12 }}>
                            {label} ({data.count})
                          </td>
                          <TdNum style={{ fontWeight:bold?700:600, color:B.cyanDark }}>{fmt(data.principal)}</TdNum>
                          <TdNum style={{ fontWeight:bold?700:600, color:B.cyanDark }}>{fmt(data.interest)}</TdNum>
                          <td />
                          <TdNum style={{ fontWeight:bold?700:600, color:B.cyanDark }}>{fmt(data.fee)}</TdNum>
                          <td />
                          <TdNum style={{ fontWeight:bold?700:600, color:B.cyanDark }}>{fmt(data.other)}</TdNum>
                          <TdNum style={{ fontWeight:bold?700:600, color:B.cyanDark }}>{fmt(data.passthru)}</TdNum>
                          <TdNum style={{ fontWeight:bold?700:600, color:B.cyanDark }}>{fmt(data.total)}</TdNum>
                          <TdNum style={{ fontWeight:bold?700:600, color:B.cyanDark }}>{fmt(data.received)}</TdNum>
                          <td />
                        </tr>
                      );

                      const spDateVal = (s) => { const v = spv(s,'scheduleddate'); return v ? String(v).slice(0,10) : ""; };
                      // ── inline-edit row (Administrator grid) ──
                      const spEditRow = (s, loan) => {
                        const changed = spRowChanged(s);
                        return (
                        <tr key={s.id} style={{ background: changed ? "rgba(18,181,219,0.06)" : "" }}>
                          <Td><span style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{loan.loannumber}</span></Td>
                          <Td><Badge s={s.unscheduledpayment ? "Unscheduled" : "Scheduled"} /></Td>
                          <Td><input type="date" value={spDateVal(s)} onChange={e=>setSpField(s.id,'scheduleddate',e.target.value)}
                            style={{ padding:"4px 6px", fontSize:12, border:"1px solid var(--color-border-secondary)", borderRadius:6, background:"var(--color-background-primary)", color:"var(--color-text-primary)" }} /></Td>
                          <Td>{s.status ? <Badge s={s.status} /> : <span style={{ color:"var(--color-text-secondary)" }}>—</span>}</Td>
                          <TdNum><FInputNum value={spv(s,'scheduledprincipal') ?? ""} onChange={v=>setSpField(s.id,'scheduledprincipal',v)} /></TdNum>
                          <TdNum><FInputNum value={spv(s,'scheduledinterest') ?? ""} onChange={v=>setSpField(s.id,'scheduledinterest',v)} /></TdNum>
                          <Td><FInput value={spv(s,'feetype') ?? ""} onChange={v=>setSpField(s.id,'feetype',v)} /></Td>
                          <TdNum><FInputNum value={spv(s,'scheduledfee') ?? ""} onChange={v=>setSpField(s.id,'scheduledfee',v)} /></TdNum>
                          <Td><FInput value={spv(s,'othertype') ?? ""} onChange={v=>setSpField(s.id,'othertype',v)} /></Td>
                          <TdNum><FInputNum value={spv(s,'scheduledother') ?? ""} onChange={v=>setSpField(s.id,'scheduledother',v)} /></TdNum>
                          <TdNum><FInputNum value={spv(s,'scheduledpassthrough') ?? ""} onChange={v=>setSpField(s.id,'scheduledpassthrough',v)} /></TdNum>
                          <TdNum style={{ fontWeight:500 }}>{fmt(spLiveTotal(s))}</TdNum>
                          <TdNum>{fmt(s.totalreceivedpmt||0)}</TdNum>
                          <Td>{changed ? <span title="Edited — unsaved" style={{ color:B.cyanDark, fontWeight:700 }}>●</span> : ""}</Td>
                        </tr>
                        );
                      };
                      const spViewRow = (s, loan) => (
                        <tr key={s.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"spmt-view",rec:{...s,id:s.id,loanid:s.loanid}})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                          <Td><span style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{loan.loannumber}</span></Td>
                          <Td><Badge s={s.unscheduledpayment ? "Unscheduled" : "Scheduled"} /></Td>
                          <Td>{fmtDate(s.scheduleddate)}</Td>
                          <Td>{s.status ? <Badge s={s.status} /> : <span style={{ color:"var(--color-text-secondary)" }}>—</span>}</Td>
                          <TdNum>{fmt(s.scheduledprincipal||0)}</TdNum>
                          <TdNum>{fmt(s.scheduledinterest||0)}</TdNum>
                          <Td>{s.feetype||"—"}</Td>
                          <TdNum>{fmt(s.scheduledfee||0)}</TdNum>
                          <Td>{s.othertype||"—"}</Td>
                          <TdNum>{fmt(s.scheduledother||0)}</TdNum>
                          <TdNum>{fmt(s.scheduledpassthrough||0)}</TdNum>
                          <TdNum style={{ fontWeight:500 }}>{fmt(s.totalscheduledpayment||0)}</TdNum>
                          <TdNum>{fmt(s.totalreceivedpmt||0)}</TdNum>
                          <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                            <Btn onClick={()=>setModal({type:"spmt-view",rec:{...s,id:s.id,loanid:s.loanid}})}><Icon type="view" /></Btn>
                            {role.canEdit&&<Btn onClick={()=>setModal({type:"spmt-edit",rec:{...s,id:s.id,loanid:s.loanid},onDone:load})}><Icon type="edit" /></Btn>}
                            {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete this scheduled payment?",onConfirm:()=>delRec("ScheduledPmts",s.id,"Deleted scheduled pmt",load)})} variant="danger"><Icon type="delete" /></Btn>}
                          </div></Td>
                        </tr>
                      );
                      const spRow = (s, loan) => spEdit ? spEditRow(s, loan) : spViewRow(s, loan);

                      const allSpmts = loans.flatMap(loan =>
                        filtSpmts.filter(s => s.loanid === loan.id || s.loannumber === loan.loannumber)
                      );
                      const grandTotals = spTotals(allSpmts);

                      return <>
                        {loans.map(loan => {
                          // In the order of the column sort (scheduled date by default), within the loan.
                          const loanSpmts = sortedSpmts
                            .filter(s => s.loanid === loan.id || s.loannumber === loan.loannumber);
                          if (loanSpmts.length === 0) return null;

                          const unscheduled = loanSpmts.filter(s => s.unscheduledpayment);
                          const scheduled   = loanSpmts.filter(s => !s.unscheduledpayment);
                          const unschedTotals = spTotals(unscheduled);
                          const schedTotals   = spTotals(scheduled);
                          const loanTotals    = spTotals(loanSpmts);

                          return <>
                            {/* ── Loan header ── */}
                            <tr key={"hdr-"+loan.id}>
                              <td colSpan={99} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
                                {loan.loannumber} — {loan.facilitytype||loan.Type} — <Badge s={loan.loanstatus} />
                              </td>
                            </tr>

                            {/* ── Unscheduled subgroup ── */}
                            {unscheduled.length>0&&<>
                              <tr key={"unsched-hdr-"+loan.id}>
                                <td colSpan={99} style={{ padding:"6px 12px 6px 24px", fontSize:11, fontWeight:600, color:B.amberDark, background:B.amberLight, borderLeft:"3px solid "+B.amber }}>
                                  Unscheduled — {unscheduled.length} record{unscheduled.length!==1?"s":""}
                                </td>
                              </tr>
                              {unscheduled.map(s=>spRow(s,loan))}
                              <SubtotalRow label="Unscheduled Subtotal" data={unschedTotals} indent bg={B.amberLight} borderColor={B.amberMid} />
                            </>}

                            {/* ── Scheduled subgroup ── */}
                            {scheduled.length>0&&<>
                              <tr key={"sched-hdr-"+loan.id}>
                                <td colSpan={99} style={{ padding:"6px 12px 6px 24px", fontSize:11, fontWeight:600, color:B.cyanDark, background:"rgba(18,181,219,0.08)", borderLeft:"3px solid "+B.cyan }}>
                                  Scheduled — {scheduled.length} record{scheduled.length!==1?"s":""}
                                </td>
                              </tr>
                              {scheduled.map(s=>spRow(s,loan))}
                              <SubtotalRow label="Scheduled Subtotal" data={schedTotals} indent bg="rgba(18,181,219,0.06)" borderColor={B.cyan} />
                            </>}

                            {/* ── Loan total ── */}
                            <SubtotalRow label={loan.loannumber+" Total"} data={loanTotals} bg={B.cyanLight} borderColor={B.cyan} />
                          </>;
                        })}

                        {/* ── Grand total ── */}
                        <tr style={{ background:B.cyanLight, borderTop:"2px solid "+B.cyan }}>
                          <td colSpan={4} style={{ padding:"10px 12px", fontSize:13, fontWeight:700, color:B.cyanDark, textAlign:"right" }}>
                            Grand Total ({grandTotals.count})
                          </td>
                          <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grandTotals.principal)}</TdNum>
                          <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grandTotals.interest)}</TdNum>
                          <td />
                          <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grandTotals.fee)}</TdNum>
                          <td />
                          <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grandTotals.other)}</TdNum>
                          <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grandTotals.passthru)}</TdNum>
                          <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grandTotals.total)}</TdNum>
                          <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grandTotals.received)}</TdNum>
                          <td />
                        </tr>
                      </>;
                    })()}
                  </tbody>
                </table>
              </Card>
            </>}

          {/* ── Received Pmts ── */}
              {tab==="rpmts"&&<>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, flexWrap:"wrap", gap:8 }}>
                  <SearchBar value={qRpmts} onChange={v=>{ setQRpmts(v); resetRpmtPage(); }} placeholder="Search received payments..." />
                  <Btn onClick={()=>exportCSV(sortedRpmts,["loannumber","receiveddate","paymentapplieddate","receivedprincipal","receivedinterest","receivedfee","receivedother","receivedpassthrough","totalreceivedpmt"],"received_pmts.csv")}>Export CSV</Btn>
                </div>

                {(()=>{
                  const rpmtTotals = arr => ({
                    count:      arr.length,
                    principal:  arr.reduce((s,r)=>s+(parseFloat(r.receivedprincipal)||0),0),
                    interest:   arr.reduce((s,r)=>s+(parseFloat(r.receivedinterest)||0),0),
                    fee:        arr.reduce((s,r)=>s+(parseFloat(r.receivedfee)||0),0),
                    other:      arr.reduce((s,r)=>s+(parseFloat(r.receivedother)||0),0),
                    passthru:   arr.reduce((s,r)=>s+(parseFloat(r.receivedpassthrough)||0),0),
                    total:      arr.reduce((s,r)=>s+(parseFloat(r.totalreceivedpmt)||0),0),
                  });

                  const RpmtSubtotalRow = ({ label, data, bold=false, bg="var(--color-background-secondary)", borderColor=B.cyanLight }) => (
                    <tr style={{ background:bg, borderTop:"1.5px solid "+borderColor }}>
                      <td colSpan={3} style={{ padding:"7px 12px", fontSize:12, fontWeight:bold?700:600, color:B.cyanDark, textAlign:"right" }}>
                        {label} ({data.count})
                      </td>
                      <TdNum style={{ fontWeight:bold?700:600, color:B.cyanDark }}>{fmt(data.principal)}</TdNum>
                      <TdNum style={{ fontWeight:bold?700:600, color:B.cyanDark }}>{fmt(data.interest)}</TdNum>
                      <TdNum style={{ fontWeight:bold?700:600, color:B.cyanDark }}>{fmt(data.fee)}</TdNum>
                      <TdNum style={{ fontWeight:bold?700:600, color:B.cyanDark }}>{fmt(data.other)}</TdNum>
                      <TdNum style={{ fontWeight:bold?700:600, color:B.cyanDark }}>{fmt(data.passthru)}</TdNum>
                      <TdNum style={{ fontWeight:bold?700:600, color:B.cyanDark }}>{fmt(data.total)}</TdNum>
                      <td />
                    </tr>
                  );

                  // Group by loannumber preserving sort order
                  const loanNumbers = [...new Set(sortedRpmts.map(r=>r.loannumber))];
                  const rpmtGroups  = loanNumbers.map(loannumber=>({
                    loannumber,
                    rows: displayedRpmts.filter(r=>r.loannumber===loannumber)
                  })).filter(g=>g.rows.length>0);

                  const grandTotals = rpmtTotals(sortedRpmts);

                  return <Card style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
                      <thead><tr>
                        <STh label="Loan #"        sortKey="loannumber"          currentKey={rpmtSortKey} currentDir={rpmtSortDir} onSort={rpmtToggle} />
                        <STh label="Received Date" sortKey="receiveddate"        currentKey={rpmtSortKey} currentDir={rpmtSortDir} onSort={rpmtToggle} />
                        <STh label="Applied Date"  sortKey="paymentapplieddate"  currentKey={rpmtSortKey} currentDir={rpmtSortDir} onSort={rpmtToggle} />
                        <STh label="Principal"     sortKey="receivedprincipal"   currentKey={rpmtSortKey} currentDir={rpmtSortDir} onSort={rpmtToggle} numeric />
                        <STh label="Interest"      sortKey="receivedinterest"    currentKey={rpmtSortKey} currentDir={rpmtSortDir} onSort={rpmtToggle} numeric />
                        <STh label="Fee"           sortKey="receivedfee"         currentKey={rpmtSortKey} currentDir={rpmtSortDir} onSort={rpmtToggle} numeric />
                        <STh label="Other"         sortKey="receivedother"       currentKey={rpmtSortKey} currentDir={rpmtSortDir} onSort={rpmtToggle} numeric />
                        <STh label="Pass-through"  sortKey="receivedpassthrough" currentKey={rpmtSortKey} currentDir={rpmtSortDir} onSort={rpmtToggle} numeric />
                        <STh label="Total"         sortKey="totalreceivedpmt" currentKey={rpmtSortKey} currentDir={rpmtSortDir} onSort={rpmtToggle} numeric />
                        <Th></Th>
                      </tr></thead>
                      <tbody>
                        {rpmtGroups.map(group=>{
                          const loan = loans.find(l=>l.loannumber===group.loannumber);
                          const sub  = rpmtTotals(group.rows);
                          return <>
                            {/* ── Loan group header ── */}
                            <tr key={"hdr-"+group.loannumber}>
                              <td colSpan={99} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
                                {group.loannumber}
                                {loan&&<> — {loan.facilitytype} — <Badge s={loan.loanstatus} /></>}
                                <span style={{ marginLeft:8, fontSize:11, fontWeight:400 }}>{sub.count} payment{sub.count!==1?"s":""}</span>
                              </td>
                            </tr>

                            {/* ── Group rows ── */}
                            {group.rows.map(r=>(
                              <tr key={r.id} style={{ cursor:"pointer" }}
                                onClick={()=>setModal({type:"rpmt-view",rec:{...r,id:r.id,scheduledPmtId:r.ScheduledPmtId,receiveddate:r.receiveddate,receivedprincipal:r.receivedprincipal,receivedinterest:r.receivedinterest,receivedfee:r.receivedfee,receivedpassthrough:r.receivedpassthrough,receivedother:r.receivedother,reversaldate:r.reversaldate,paymentapplieddate:r.paymentapplieddate,totalreceivedpmt:r.totalreceivedpmt,record_id_deprecated:r.record_id_deprecated}})}
                                onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"}
                                onMouseLeave={e=>e.currentTarget.style.background=""}>
                                <Td><span style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{r.loannumber}</span></Td>
                                <Td>{fmtDate(r.receiveddate)}</Td>
                                <Td>{fmtDate(r.paymentapplieddate)}</Td>
                                <TdNum>{fmt(r.receivedprincipal)}</TdNum>
                                <TdNum>{fmt(r.receivedinterest)}</TdNum>
                                <TdNum>{fmt(r.receivedfee)}</TdNum>
                                <TdNum>{fmt(r.receivedother)}</TdNum>
                                <TdNum>{fmt(r.receivedpassthrough)}</TdNum>
                                <TdNum style={{ fontWeight:500 }}>{fmt(r.totalreceivedpmt)}</TdNum>
                                <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                                  <Btn onClick={()=>setModal({type:"rpmt-view",rec:{...r,id:r.id,scheduledPmtId:r.ScheduledPmtId,receiveddate:r.receiveddate,receivedprincipal:r.receivedprincipal,receivedinterest:r.receivedinterest,receivedfee:r.receivedfee,receivedpassthrough:r.receivedpassthrough,receivedother:r.receivedother,reversaldate:r.reversaldate,paymentapplieddate:r.paymentapplieddate,totalreceivedpmt:r.totalreceivedpmt,record_id_deprecated:r.record_id_deprecated}})}><Icon type="view" /></Btn>
                                  {role.isservicer&&<Btn onClick={()=>setModal({type:"rpmt-edit",rec:{...r,id:r.id,scheduledPmtId:r.ScheduledPmtId,receiveddate:r.receiveddate,receivedprincipal:r.receivedprincipal,receivedinterest:r.receivedinterest,receivedfee:r.receivedfee,receivedpassthrough:r.receivedpassthrough,receivedother:r.receivedother,reversaldate:r.reversaldate,paymentapplieddate:r.paymentapplieddate,totalreceivedpmt:r.totalreceivedpmt,record_id_deprecated:r.record_id_deprecated},onDone:load})}><Icon type="edit" /></Btn>}
                                  {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete this received payment?",onConfirm:()=>delRec("ReceivedPmts",r.id,"Deleted received payment",load)})} variant="danger"><Icon type="delete" /></Btn>}
                                </div></Td>
                              </tr>
                            ))}

                            {/* ── Group subtotal ── */}
                            <RpmtSubtotalRow label={group.loannumber+" Subtotal"} data={sub} bg="rgba(18,181,219,0.06)" borderColor={B.cyan} />
                          </>;
                        })}

                        {/* ── Grand total ── */}
                        <tr style={{ background:B.cyanLight, borderTop:"2px solid "+B.cyan }}>
                          <td colSpan={3} style={{ padding:"10px 12px", fontSize:13, fontWeight:700, color:B.cyanDark, textAlign:"right" }}>
                            Grand Total ({grandTotals.count} payment{grandTotals.count!==1?"s":""})
                          </td>
                          <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grandTotals.principal)}</TdNum>
                          <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grandTotals.interest)}</TdNum>
                          <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grandTotals.fee)}</TdNum>
                          <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grandTotals.other)}</TdNum>
                          <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grandTotals.passthru)}</TdNum>
                          <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grandTotals.total)}</TdNum>
                          <td />
                        </tr>
                      </tbody>
                    </table>
                    <Pagination page={rpmtPage} totalPages={rpmtTotalPages} total={rpmtTotal} pageSize={50} onPage={setRpmtPage} />
                  </Card>;
                })()}
              </>}
          {tab==="bankaccounts"&&(()=>{
            const isAdminRole = role.isAdmin || role.name === "Admin"; // adapt to your role object

            const groups = [
              ...borrowersGuarantors.map(bg=>({
                key: bg.id,
                label: bg.borrowerguarantorname + " — " + (bg.borrowerguarantor||"") ,
                rows: sortedBanks.filter(b=>b.borrowerid===bg.id)
              })),
              { key:"__none", label:"Unassigned", rows: sortedBanks.filter(b=>!b.borrowerid) }
            ].filter(g=>g.rows.length>0);

            return <>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, flexWrap:"wrap", gap:8 }}>
                <div style={{ fontSize:12, color:"var(--color-text-secondary)" }}>
                  Account and routing numbers are masked. {isAdminRole ? "Credentials visible (Admin)." : "Credentials are Admin-only."}
                </div>
                {role.canEdit&&<Btn onClick={()=>setModal({type:"bankaccount-add",accountid:account?.id,onDone:load})} variant="primary">+ Add Bank Account</Btn>}
              </div>

              <Card style={{ overflowX:"auto" }}>
                {bankAccounts.length===0
                  ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:12 }}>No bank accounts recorded.</div>
                  : <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
                      <thead><tr>
                        <STh label="Primary" sortKey="primaryaccount" currentKey={baSK} currentDir={baSD} onSort={baToggle} />
                        <STh label="Bank" sortKey="bankname" currentKey={baSK} currentDir={baSD} onSort={baToggle} />
                        <STh label="Account #" sortKey="accountnumberlast4" currentKey={baSK} currentDir={baSD} onSort={baToggle} />
                        <STh label="ACH Routing" sortKey="routingnumberachlast4" currentKey={baSK} currentDir={baSD} onSort={baToggle} />
                        <STh label="Wire Routing" sortKey="routingnumberwirelast4" currentKey={baSK} currentDir={baSD} onSort={baToggle} />
                        <STh label="Lockbox" sortKey="lockbox" currentKey={baSK} currentDir={baSD} onSort={baToggle} />
                        <STh label="Lockbox Ref" sortKey="lockboxreference" currentKey={baSK} currentDir={baSD} onSort={baToggle} />
                        <STh label="Control Agmt" sortKey="controlagreement" currentKey={baSK} currentDir={baSD} onSort={baToggle} />
                        <STh label="CA Type" sortKey="controlagreementtype" currentKey={baSK} currentDir={baSD} onSort={baToggle} />
                        <STh label="Closed" sortKey="accountcloseddate" currentKey={baSK} currentDir={baSD} onSort={baToggle} />
                        <Th>Bank URL</Th>{/* a link, present or not — nothing to order by */}
                        <Th></Th>
                      </tr></thead>
                      <tbody>
                        {groups.map(g=>(
                          <>
                            <tr key={"hdr-"+g.key}>
                              <td colSpan={99} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
                                {g.label}
                                <span style={{ marginLeft:8, fontSize:11, fontWeight:400 }}>{g.rows.length} account{g.rows.length!==1?"s":""}</span>
                              </td>
                            </tr>
                            {g.rows.map(b=>(
                              <tr key={b.id} style={{ cursor:"pointer", opacity: b.accountcloseddate ? 0.55 : 1 }}
                                onClick={()=>setModal({type:"bankaccount-view",rec:{...b,id:b.id,accountid:b.accountid,borrowerid:b.borrowerid}})}
                                onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"}
                                onMouseLeave={e=>e.currentTarget.style.background=""}>
                                <Td style={{ textAlign:"center" }}>{b.primaryaccount ? <span style={{ color:B.amberDark, fontWeight:700 }}>★</span> : "—"}</Td>
                                <Td style={{ fontWeight:500 }}>{b.bankname||"—"}</Td>
                                <Td><span style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{b.accountnumberlast4 ? "••••"+b.accountnumberlast4 : "—"}</span></Td>
                                <Td><span style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{b.routingnumberachlast4 ? "•••••"+b.routingnumberachlast4 : "—"}</span></Td>
                                <Td><span style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{b.routingnumberwirelast4 ? "•••••"+b.routingnumberwirelast4 : "—"}</span></Td>
                                <Td>{b.lockbox?"✓":"—"}</Td>
                                <Td>{b.lockboxreference||"—"}</Td>
                                <Td>{b.controlagreement?"✓":"—"}</Td>
                                <Td>{b.controlagreementtype ? <Badge s={b.controlagreementtype} /> : "—"}</Td>
                                <Td>{b.accountcloseddate?fmtDate(b.accountcloseddate):"—"}</Td>
                                <Td onClick={e=>e.stopPropagation()}>
                                  {b.bankurl ? <a href={b.bankurl} target="_blank" rel="noreferrer" style={{ color:B.cyan }}>Link</a> : "—"}
                                </Td>
                                <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                                  <Btn onClick={()=>setModal({type:"bankaccount-view",rec:{...b,id:b.id,accountid:b.accountid,borrowerid:b.borrowerid}})}><Icon type="view" /></Btn>
                                  {role.canEdit&&<Btn onClick={()=>setModal({type:"bankaccount-edit",rec:{...b,id:b.id,accountid:b.accountid,borrowerid:b.borrowerid},onDone:load})}><Icon type="edit" /></Btn>}
                                  {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete this bank account?",onConfirm:()=>delRec("BankAccounts",b.id,"Deleted bank account",load)})} variant="danger"><Icon type="delete" /></Btn>}
                                </div></Td>
                              </tr>
                            ))}
                          </>
                        ))}
                      </tbody>
                    </table>}
              </Card>

              {/* Rendered only for those who can actually read it. Without this
                  guard the section still mounted for everyone and fetched, so a
                  user without the flag got two red error boxes with Retry
                  buttons — the data was safe, but the page said "broken" when
                  it meant "not yours". The endpoints enforce the same rule
                  independently; this only decides whether to ASK. */}
              {role.canViewBankFeeds && <PlaidBankCards accountId={view.accountid} />}
            </>;
          })()}

          {/* ── Legal Events ── */}
          {tab==="legal"&&<>
            <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:12 }}>
              {role.canEdit&&<Btn onClick={()=>setModal({type:"legalevent-add",accountid:account.id,onDone:load})} variant="primary">+ Add Legal Event</Btn>}
            </div>
            <Card>
              {legalEvents.length===0
                ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:12 }}>No legal events recorded.</div>
                : <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    
                    <thead><tr>
                      <STh label="Type" sortKey="type" currentKey={leSK} currentDir={leSD} onSort={leToggle} />
                      <STh label="Level" sortKey="_level" currentKey={leSK} currentDir={leSD} onSort={leToggle} />
                      <STh label="Loan #" sortKey="loannumber" currentKey={leSK} currentDir={leSD} onSort={leToggle} />
                      <STh label="Start Date" sortKey="startdate" currentKey={leSK} currentDir={leSD} onSort={leToggle} />
                      <STh label="End Date" sortKey="enddate" currentKey={leSK} currentDir={leSD} onSort={leToggle} />
                      <Th></Th></tr></thead>
                    <tbody>{sortedLegal.map(e=>(
                      <tr key={e.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"legalevent-view",rec:{...e,id:e.id,accountid:e.accountid,loanid:e.loanid||null},onDone:load})}
                        onMouseEnter={ev=>ev.currentTarget.style.background="var(--color-background-secondary)"}
                        onMouseLeave={ev=>ev.currentTarget.style.background=""}>
                        <Td><Badge s={e.type} /></Td>
                        <Td><span style={{ fontSize:11, padding:"2px 8px", borderRadius:4, fontWeight:500,
                          background: e.loanid ? B.amberLight : B.cyanLight,
                          color: e.loanid ? B.amberDark : B.cyanDark }}>
                          {e.loanid ? "Loan" : "Account"}
                        </span></Td>
                        <Td><span style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{e.loannumber||"—"}</span></Td>
                        <Td>{fmtDate(e.startdate)}</Td>
                        <Td>{e.enddate?fmtDate(e.enddate):"Ongoing"}</Td>
                        <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={ev=>ev.stopPropagation()}>
                          <Btn onClick={()=>setModal({type:"legalevent-view",rec:{...e,id:e.id,accountid:e.accountid,loanid:e.loanid||null}})}><Icon type="view" /></Btn>
                          {role.canEdit&&<Btn onClick={()=>setModal({type:"legalevent-edit",rec:{...e,id:e.id,accountid:e.accountid,loanid:e.loanid||null},onDone:load})}><Icon type="edit" /></Btn>}
                          {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete this legal event?",onConfirm:()=>delRec("LegalEvents",e.id,"Deleted legal event",load)})} variant="danger"><Icon type="delete" /></Btn>}
                        </div></Td>
                      </tr>
                    ))}
                    </tbody>
                  </table>}
            </Card>
          </>}
          {tab==="statements" && <StatementsTab statements={statements} role={role} api={api} reload={load} delRec={delRec} setConfirm={setConfirm} />}
          {/* Notes and Emails sit in a card like every other tab, not on the tab background. */}
          {tab === "notes" && <Card><NotesPanel scope="account" entitytype="Account" entityid={account.id} accountid={account.id} source="Account" /></Card>}
          {/* rollup: every message captured against this account AND all of
              its contacts, deduped to one row per message. */}
          {tab === "emails" && canSeeEmails && <Card>
            <PanelTitle title="Emails" />
            <EmailActivityPanel accountid={account.id} rollup />
          </Card>}

        </div>
      </div>
    </>;
  };
